import React, { useEffect, useMemo, useState } from 'react'
import { api } from './api.js'
import { getApiKey, RENDER_DPI, setApiKey } from './config.js'
import PageCanvas from './PageCanvas.jsx'
import formDescriptions from '../forme-descrizioni-it.json'

// Worklist di campioni di default (modificabile a runtime, persistita in localStorage).
const DEFAULT_WORKLIST = [
  { doc: '23382', hint: 'Ragione Sociale (A=D, B diverso) -> A.O.U. DEGLI STUDI DELLA CAMPANIA L.VANVITELLI' },
  { doc: '23626', hint: 'Data/Riferimento Vs Ordine (A/D vuoti) -> 30/01/2025 - NIPPON GASES PHARMA S.R.L.' },
  { doc: '23629', hint: 'Data/Riferimento Vs Ordine -> 30/06/2025 - NIPPON GASES PHARMA S.R.L.' },
  { doc: '248251', hint: 'Data/Riferimento Vs Ordine -> 26/06/2025 - NIPPON GASES PHARMA S.R.L.' },
  { doc: '369441', hint: 'Data/Riferimento Vs Ordine -> 19/01/2026 - CASA DI CURA C.G.RUESCH S.P.A.' },
  { doc: '755656', hint: 'Data Vs Ordine -> 28/04/2026' },
  { doc: '1331539', hint: 'Partita Iva -> 94194310630' },
]
const WL_KEY = 'ttai_onboarding_worklist'          // legacy (lista piatta) - solo per migrazione
const WLS_KEY = 'ttai_onboarding_worklists'         // nuovo: liste campioni PER CLASSE
const PANEL_W_KEY = 'ttai_onboarding_panelw'
const PANEL_H_KEY = 'ttai_onboarding_panelh'
const DOCK_KEY = 'ttai_onboarding_dock'
const THEME_KEY = 'ttai_onboarding_theme'
const ZONE_KEY = 'ttai_onboarding_zonegrow'
const MULTI_WORD_FORMS = new Set(['ragione_sociale', 'indirizzo', 'descrizione'])
const FORM_DESCRIPTIONS = formDescriptions.forme || {}
const NO_FORM_DESCRIPTION = formDescriptions.senza_forma || { descrizione: 'Nessun tipo dichiarato.' }
const OPTIONAL_EMPTY_FORMS = new Set(['cig', 'cup', 'codice_ipa'])
const EMPTY_ARITH_DRAFT = { op: 'signed_sum', target_role: '', terms: [], rows_role: '', tolerance: 0.01 }
const DATE_CONVENTIONS = [
  { value: 'it_dmy', label: 'IT giorno/mese/anno' },
  { value: 'en_mdy', label: 'EN mese/giorno/anno' },
  { value: 'iso_ymd', label: 'ISO anno/mese/giorno' },
]
const DATE_CONVENTION_LABELS = Object.fromEntries(DATE_CONVENTIONS.map((x) => [x.value, x.label]))
const LABEL_DIRECTIONS = [
  { value: "omni", label: "omni", title: "Valore piu vicino in qualunque direzione" },
  { value: "right", label: "destra", title: "Valore sulla stessa riga a destra della label" },
  { value: "left", label: "sinistra", title: "Valore sulla stessa riga a sinistra della label" },
  { value: "down", label: "sotto", title: "Valore sotto piu vicino, anche se spostato a sinistra o destra" },
  { value: "up", label: "sopra", title: "Valore sopra piu vicino, anche se spostato a sinistra o destra" },
]
const LABEL_DIRECTION_LABELS = Object.fromEntries(LABEL_DIRECTIONS.map((x) => [x.value, x.label]))
function normalizeLabelDirection(value) {
  const v = normText(value)
  return LABEL_DIRECTION_LABELS[v] ? v : ""
}
function describeLabelDirection(value) {
  const direction = normalizeLabelDirection(value)
  if (direction === "down") return "sotto: primo valore vicino sotto la label, anche fuori asse X."
  if (direction === "up") return "sopra: primo valore vicino sopra la label, anche fuori asse X."
  if (direction === "left") return "sinistra: stessa riga a sinistra della label."
  if (direction === "right") return "destra: stessa riga a destra della label."
  if (direction === "omni") return "omni: valore piu vicino alla label."
  return "legacy: comportamento storico senza direzione esplicita."
}

function buildFormGroups(forms, areaOrder, areaLabels) {
  const entries = Array.isArray(forms) ? forms : []
  const order = Array.isArray(areaOrder) && areaOrder.length ? areaOrder : ['generiche']
  const labels = areaLabels || {}
  const buckets = new Map()
  for (const item of entries) {
    const area = item?.area || 'generiche'
    if (!buckets.has(area)) buckets.set(area, [])
    buckets.get(area).push(item)
  }
  const extraAreas = Array.from(buckets.keys()).filter((area) => !order.includes(area)).sort((a, b) => a.localeCompare(b, 'it'))
  return [...order, ...extraAreas]
    .filter((area) => buckets.has(area))
    .map((area) => ({
      area,
      label: labels[area] || (area === 'generiche' ? 'Generiche' : area),
      forms: buckets.get(area).slice().sort((a, b) => {
        if (a.form === 'testo_generico') return -1
        if (b.form === 'testo_generico') return 1
        return String(a.form || '').localeCompare(String(b.form || ''), 'it')
      }),
    }))
}

// Mappa { "<classe>": [ {doc, hint} ], "__default__": [...] }. __default__ = fallback per le classi senza lista propria.
function loadWorklists() {
  try { const s = window.localStorage.getItem(WLS_KEY); if (s) return JSON.parse(s) } catch { /* ignore */ }
  try { const o = window.localStorage.getItem(WL_KEY); if (o) return { __default__: JSON.parse(o) } } catch { /* ignore */ }
  return { __default__: DEFAULT_WORKLIST }
}
function saveWorklists(map) {
  try { window.localStorage.setItem(WLS_KEY, JSON.stringify(map)) } catch { /* ignore */ }
}
// Accetta { "<classe>": [...] } oppure { "classes": { "<classe>": [...] } }; ogni voce e' "docnumber" o {doc,hint}.
function normalizeWorklistJson(raw) {
  const map = (raw && typeof raw === 'object' && raw.classes) ? raw.classes : raw
  const out = {}
  for (const [cl, arr] of Object.entries(map || {})) {
    if (!Array.isArray(arr)) continue
    out[cl] = arr.map((it) => (typeof it === 'string' || typeof it === 'number')
      ? { doc: String(it).replace(/\D/g, ''), hint: '' }
      : { doc: String(it.doc ?? it.docnumber ?? '').replace(/\D/g, ''), hint: it.hint || '' })
      .filter((it) => it.doc)
  }
  return out
}

// Euristica "solo testata": nasconde SOLO il contenitore ClasseBox (CLASSE*) e le sue colonne
// annidate (nome con "."). NON nasconde i campi singoli (anche se _129, es. TESTO131_129 = "Codice Iva"):
// potrebbero essere valori che l'operatore vuole marcare. Provvisorio finche' cmp_field_map non porta lo scope.
function isRigaField(fieldName) {
  const n = String(fieldName || '')
  return /^CLASSE/i.test(n) || n.includes('.')
}
const FIELD_LIST_GROUPS = [
  { key: 'standard', label: 'Standard' },
  { key: 'additional', label: 'Aggiuntivi' },
  { key: 'rows', label: 'Righe' },
]
function fieldDisplayLabel(f) {
  return (f?.arxivar_label && String(f.arxivar_label).trim())
    || (f?.arxivar_external_id && String(f.arxivar_external_id).trim())
    || f?.field_name
    || ''
}
function fieldIdentity(f) {
  return (f?.arxivar_external_id && String(f.arxivar_external_id).trim())
    || f?.field_name
    || ''
}
const TESTATA_INHERIT_PREFIX = 'testata:'
function inheritedTestataFieldName(...records) {
  for (const rec of records || []) {
    const source = cleanText(rec?.form_source)
    if (source.toLowerCase().startsWith(TESTATA_INHERIT_PREFIX)) {
      return source.slice(TESTATA_INHERIT_PREFIX.length).trim()
    }
  }
  return ''
}
function inheritOptionLabel(f) {
  return `${fieldDisplayLabel(f)} (${fieldIdentity(f)})`
}
function fieldListGroupKey(f) {
  if (isRigaField(f?.field_name)) return 'rows'
  const kind = normText(f?.field_kind || f?.kind)
  const source = normText(f?.arxivar_source)
  if (kind === 'standard' || source === 'dmprofile' || source === 'dm_profile') return 'standard'
  return 'additional'
}
function isSystemField(f) {
  const v = f?.is_system
  return v === true || v === 1 || v === '1'
}
function buildFieldListGroups(fields, { showDone, onlyTestata, isDone }) {
  const buckets = new Map(FIELD_LIST_GROUPS.map((group) => [group.key, []]))
  for (const f of fields || []) {
    if (isSystemField(f)) continue
    if (!showDone && isDone(f)) continue
    if (onlyTestata && isRigaField(f.field_name)) continue
    buckets.get(fieldListGroupKey(f))?.push(f)
  }
  return FIELD_LIST_GROUPS
    .map((group) => ({
      ...group,
      items: (buckets.get(group.key) || []).slice().sort((a, b) => {
        const la = fieldDisplayLabel(a)
        const lb = fieldDisplayLabel(b)
        return la.localeCompare(lb, 'it', { sensitivity: 'base', numeric: true })
          || fieldIdentity(a).localeCompare(fieldIdentity(b), 'it', { sensitivity: 'base', numeric: true })
      }),
    }))
    .filter((group) => group.items.length)
}
function isProfileActive(row) {
  return row?.profilo_attivo !== null && row?.profilo_attivo !== undefined && row?.profilo_attivo !== ''
}
function externalChip(check) {
  if (!check) return { tone: 'unknown', text: 'external_id: da verificare' }
  if (check.loading) return { tone: 'unknown', text: 'external_id: verifico' }
  if (check.error) return { tone: 'missing', text: 'external_id: errore verifica' }
  const hasFieldCount = Object.prototype.hasOwnProperty.call(check, 'n_fields')
  if (check.catalog_missing || (hasFieldCount && Number(check.n_fields || 0) === 0)) {
    return { tone: 'missing', text: 'catalogo campi non generato - premi Dichiara classe' }
  }
  if (check.ok) return { tone: 'ok', text: 'external_id: completo' }
  if (check.child_schema_missing) return { tone: 'missing', text: 'ClasseBox figlia non pronta' }
  const n = Number(check.n_missing || 0)
  return { tone: 'missing', text: `external_id: mancante (${n})` }
}
function missingExternalLabel(item) {
  const label = cleanText(item.label) || cleanText(item.field_name) || 'campo'
  const field = cleanText(item.field_name) || '-'
  const parent = cleanText(item.parent_field)
  return item.scope === 'riga' && parent ? `${parent}.${field} - ${label}` : `${field} - ${label}`
}

// Fase B: suggerimento automatico dell'ancora. Parole d'aggancio note (priorita' nel suggerimento).
const ANCHOR_HINTS = ['spett', 'cliente', 'destinatario', 'iban', 'totale', 'imponibile', 'partita',
  'piva', 'codice', 'cig', 'cup', 'data', 'provincia', 'cap', 'indirizzo', 'via', 'ragione', 'fornitore',
  'banca', 'protocollo', 'numero', 'rif', 'ordine']
const MODE_META = {
  value: { family: 'fields', label: 'Campo valore', short: 'Valore', icon: '[]', tone: 'fields' },
  area: { family: 'fields', label: 'Area geografica', short: 'Area', icon: '[]', tone: 'fields' },
  barcode: { family: 'fields', label: 'Barcode', short: 'Barcode', icon: 'BC', tone: 'fields' },
  anchor: { family: 'anchors', label: 'Ancora e perimetro', short: 'Ancora', icon: '@', tone: 'anchors' },
  gap_columns: { family: 'anchors', label: 'Colonne a vuoti', short: 'Vuoti', icon: '||', tone: 'anchors' },
  table_area: { family: 'table', label: 'Area tabella', short: 'Area tabella', icon: '#', tone: 'table' },
  column: { family: 'table', label: 'Colonna', short: 'Colonna', icon: '|', tone: 'table' },
  list: { family: 'table', label: 'Lista per forma', short: 'Lista', icon: 'L', tone: 'table' },
  footer: { family: 'table', label: 'Piede tabella', short: 'Piede', icon: '_', tone: 'table' },
  forms: { family: 'forms', label: 'Forme', short: 'Forme', icon: 'F', tone: 'forms' },
  rules: { family: 'rules', label: 'Regole', short: 'Regole', icon: '=', tone: 'rules' },
  summary: { family: 'summary', label: 'Riepilogo', short: 'Riepilogo', icon: 'i', tone: 'summary' },
}
const FAMILY_TABS = [
  { key: 'fields', label: 'Campi', icon: '[]', helpHash: 'campi' },
  { key: 'anchors', label: 'Ancore & perimetro', icon: '@', helpHash: 'label' },
  { key: 'table', label: 'Tabella', icon: '#', helpHash: 'tabella' },
  { key: 'forms', label: 'Forme', icon: 'F', helpHash: 'forme' },
  { key: 'rules', label: 'Regole', icon: '=', helpHash: 'regole' },
  { key: 'summary', label: 'Riepilogo', icon: 'i', helpHash: 'riepilogo' },
]
const PASSIVE_MODES = new Set(['forms', 'rules', 'summary'])
function _numericToken(t) { return /^[\d.,/:\-]+$/.test(String(t || '').trim()) }
// suggerisce il testo dell'ancora: parola-etichetta sopra/sinistra o immediatamente a destra del valore, con priorita' alle hint note
function suggestAnchor(box, words) {
  if (!box || !words) return null
  const cands = []
  for (const w of words) {
    const t = String(w.text || '').trim()
    if (!t || _numericToken(t)) continue
    const hOverlap = Math.min(w.x1, box.x1) - Math.max(w.x0, box.x0)
    const vOverlap = Math.min(w.y1, box.y1) - Math.max(w.y0, box.y0)
    const above = w.y1 <= box.y0 + 2 && hOverlap > 0 && (box.y0 - w.y1) < 160
    const left = w.x1 <= box.x0 + 2 && vOverlap > -4 && (box.x0 - w.x1) < 220
    const right = w.x0 >= box.x1 - 2 && vOverlap > -4 && (w.x0 - box.x1) < 140
    if (!above && !left && !right) continue
    const n = t.toLowerCase()
    const hint = ANCHOR_HINTS.some((h) => n.includes(h)) ? 1 : 0
    const d = above ? (box.y0 - w.y1) : (left ? (box.x0 - w.x1) : (w.x0 - box.x1))
    cands.push({ text: t, hint, d })
  }
  if (!cands.length) return null
  cands.sort((a, b) => (b.hint - a.hint) || (a.d - b.d))
  return cands[0].text
}

function normText(value) {
  return String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

function cleanText(value) {
  return String(value ?? '').trim()
}

function wordsText(ws) {
  return (ws || []).map((w) => String(w.text || '').trim()).filter(Boolean).join(' ')
}

function perimeterSourceLabel(perimeter) {
  const source = cleanText(perimeter?.source || perimeter?.side)
  if (source === 'end') return 'altra label'
  if (source === 'edge') {
    const direction = cleanText(perimeter?.direction)
    const scope = cleanText(perimeter?.scope)
    const names = { left: 'sinistro', right: 'destro', top: 'alto', bottom: 'basso' }
    return `bordo${direction ? ` ${names[direction] || direction}` : ''}${scope === 'doc' ? ' documento' : ''}`
  }
  if (source === 'region') return 'regione'
  if (source === 'margin') return 'margine'
  return 'non determinata'
}

function previewColumnOverlay(col) {
  const box = col?.box || col || {}
  const x0 = Number(box.x0), y0 = Number(box.y0), x1 = Number(box.x1), y1 = Number(box.y1)
  if (![x0, y0, x1, y1].every(Number.isFinite) || x1 <= x0 || y1 <= y0) return null
  return {
    index: Number(col.index),
    text: cleanText(col.text),
    kind: cleanText(col.kind),
    x0,
    y0: Math.max(0, y0 - 3),
    x1,
    y1: y1 + 3,
  }
}

function fieldLabel(f) {
  return cleanText(f?.arxivar_label) || cleanText(f?.arxivar_external_id) || cleanText(f?.field_name) || 'Campo'
}

function fieldIdent(f) {
  return cleanText(f?.arxivar_external_id) || cleanText(f?.field_name) || '-'
}

function prettyFormName(form) {
  return cleanText(form).replace(/_/g, ' ')
}

function parseAggregateAnchors(value) {
  return cleanText(value)
    .split('|')
    .map((text) => ({ anchor_text: text.trim(), anchor_role: '' }))
    .filter((a) => a.anchor_text)
}

function edgeName(text) {
  if (text === '$EDGE_RIGHT$') return 'bordo destro'
  if (text === '$EDGE_LEFT$') return 'bordo sinistro'
  if (text === '$EDGE_TOP$') return 'bordo alto (pagina)'
  if (text === '$EDGE_BOTTOM$') return 'bordo basso (pagina)'
  if (text === '$EDGE_TOP_DOC$') return 'bordo alto (documento)'
  if (text === '$EDGE_BOTTOM_DOC$') return 'bordo basso (documento)'
  return ''
}

function cardAnchors(eff, extra) {
  const detailed = Array.isArray(extra?.anchors) ? extra.anchors : []
  if (detailed.length) return detailed.filter((a) => cleanText(a.anchor_text))
  return parseAggregateAnchors(eff?.anchors)
}

function describeAnchors(anchors) {
  if (!anchors.length) return 'Nessuna ancora configurata.'
  return anchors.map((a) => {
    const text = cleanText(a.anchor_text)
    const edge = edgeName(text)
    const role = cleanText(a.anchor_role)
    if (edge) return edge
    if (role === 'footer') return `piede tabella: "${text}"`
    if (role === 'end') return `ancora di fine: "${text}"`
    return `etichetta: "${text}"`
  }).join('; ')
}

function extractDescription(extract) {
  if (!extract) return ''
  const params = extract.params || {}
  if (extract.mode === 'colonne_a_vuoti') {
    const index = params.colonna_index ?? params.column_index ?? params.index
    const expected = params.colonne_attese ?? params.expected_columns
    return `colonna ${index || '?'}${expected ? ` su ${expected}` : ''} della riga a colonne`
  }
  return 'modalita di estrazione dedicata'
}

function describePerimeter(eff, anchors, extract) {
  const modeLine = extractDescription(extract)
  if (modeLine) return modeLine.charAt(0).toUpperCase() + modeLine.slice(1) + '.'

  const nRegions = Number(eff?.n_regions || 0)
  const edges = anchors.filter((a) => edgeName(cleanText(a.anchor_text)))
  const endAnchors = anchors.filter((a) => cleanText(a.anchor_role) === 'end' && !edgeName(cleanText(a.anchor_text)))
  const textAnchors = anchors.filter((a) => !edgeName(cleanText(a.anchor_text)) && cleanText(a.anchor_role) !== 'footer')

  if (textAnchors.length && edges.length) {
    const edge = edgeName(cleanText(edges[0].anchor_text))
    if (edge.includes('alto')) return `Dalla label al ${edge}.`
    if (edge.includes('basso')) return `Dalla label al ${edge}.`
    return `Dalla label al ${edge}.`
  }
  if (textAnchors.length && endAnchors.length) return 'Tra la label e l\'ancora di fine.'
  if (textAnchors.length > 1) return 'Tra le due etichette configurate.'
  if (textAnchors.length) return 'Stessa riga, a destra della label.'
  if (nRegions > 0) return 'Nella zona marcata.'
  return 'Perimetro non ancora configurato.'
}

function describeForm(field, eff) {
  const effectiveForm = cleanText(eff?.semantic_form)
    || cleanText(field?.semantic_form)
  const source = cleanText(eff?.form_source)
    || cleanText(field?.form_source)
    || 'inferita'
  if (source.toLowerCase().startsWith(TESTATA_INHERIT_PREFIX)) {
    const inherited = source.slice(TESTATA_INHERIT_PREFIX.length).trim()
    return {
      title: 'Eredita dalla testata',
      text: inherited ? `Eredita dalla testata: ${inherited}.` : 'Eredita dalla testata.',
      note: 'Il campo-riga non viene estratto dalla riga.',
      source: 'testata_inherited',
    }
  }
  if (!effectiveForm) {
    return {
      title: 'Nessun tipo confermato',
      text: NO_FORM_DESCRIPTION.descrizione,
      note: 'La proposta inferita non e\' ancora la forma effettiva della Board.',
      source,
    }
  }
  const info = FORM_DESCRIPTIONS[effectiveForm] || {}
  return {
    title: prettyFormName(effectiveForm),
    text: info.descrizione || `Tipo valore: ${prettyFormName(effectiveForm)}.`,
    note: info.forma_tecnica ? `Forma tecnica: ${info.forma_tecnica}.` : '',
    source,
  }
}

function roleLabel(role) {
  return role === 'Dettaglio.Importo' ? 'importi di riga' : cleanText(role)
}

function ruleTerms(rule) {
  if (Array.isArray(rule?.terms)) return rule.terms
  if (rule?.terms_json) {
    try {
      const parsed = JSON.parse(rule.terms_json)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  return []
}

function formatSignedTerms(terms) {
  return (terms || [])
    .filter((t) => cleanText(t?.role))
    .map((t, i) => {
      const sign = t.sign === '-' ? '-' : '+'
      const label = roleLabel(t.role)
      if (i === 0) return sign === '-' ? `- ${label}` : label
      return `${sign === '-' ? '-' : '+'} ${label}`
    })
    .join(' ')
}

function formatRule(rule) {
  const target = cleanText(rule.target_role) || 'campo'
  const a = cleanText(rule.operand_a_role)
  const b = cleanText(rule.operand_b_role)
  const rows = cleanText(rule.rows_role)
  const terms = ruleTerms(rule)
  if (rule.op === 'signed_sum' && terms.length) return `${target} = ${formatSignedTerms(terms)}`
  if (rule.op === 'sub' && a && b) return `${target} = ${a} - ${b}`
  if (rule.op === 'add' && a && b) return `${target} = ${a} + ${b}`
  if (rule.op === 'sumrows' && rows) return `${target} = somma ${roleLabel(rows)}`
  return `${target}: regola dichiarata`
}

function arithProblems(err) {
  const payload = err?.data?.detail || err?.data || null
  return Array.isArray(payload?.problems) ? payload.problems : []
}

function arithProblemMessage(problem) {
  if (problem?.type === 'cycle') {
    const path = Array.isArray(problem.path) ? problem.path.filter(Boolean).join(' -> ') : ''
    return path ? `Regola circolare: ${path}. Non ammessa.` : 'Regola circolare non ammessa.'
  }
  if (problem?.type === 'self_reference') return 'Un valore non puo definire se stesso.'
  if (problem?.type === 'duplicate_target') return 'Esiste gia una regola per questo valore.'
  return 'Regola non valida.'
}

function arithErrorMessage(err) {
  const problems = arithProblems(err)
  if (problems.length) {
    const cycle = problems.find((p) => p.type === 'cycle')
    const selfRef = problems.find((p) => p.type === 'self_reference')
    const duplicate = problems.find((p) => p.type === 'duplicate_target')
    return arithProblemMessage(selfRef || cycle || duplicate || problems[0])
  }
  return String(err)
}

function roleValues(data) {
  return (data?.amount_roles || []).map((r) => r.role).filter(Boolean)
}

function rowRoleValues(data) {
  return (data?.row_roles || []).map((r) => r.role).filter(Boolean)
}

function defaultTerms(target, roles) {
  if (target === 'IVA' && roles.includes('Totale') && roles.includes('Imponibile')) {
    return [{ role: 'Totale', sign: '+' }, { role: 'Imponibile', sign: '-' }]
  }
  if (target === 'Totale' && roles.includes('Imponibile') && roles.includes('IVA')) {
    return [{ role: 'Imponibile', sign: '+' }, { role: 'IVA', sign: '+' }]
  }
  const available = roles.filter((r) => r !== target)
  return (available.length ? available : roles).slice(0, 2).map((role) => ({ role, sign: '+' }))
}

function defaultArithDraft(data) {
  const roles = roleValues(data)
  const target = roles.includes('IVA') ? 'IVA' : (roles[0] || '')
  return { ...EMPTY_ARITH_DRAFT, target_role: target, terms: defaultTerms(target, roles), rows_role: rowRoleValues(data)[0] || '' }
}

function normalizeArithDraft(draft, data) {
  const roles = roleValues(data)
  const rowRoles = rowRoleValues(data)
  if (!roles.length) return { ...EMPTY_ARITH_DRAFT }
  const target = roles.includes(draft.target_role) ? draft.target_role : (roles.includes('IVA') ? 'IVA' : roles[0])
  const op = draft.op === 'sumrows' && rowRoles.length ? 'sumrows' : 'signed_sum'
  const terms = (draft.terms || [])
    .filter((t) => roles.includes(t.role))
    .map((t) => ({ role: t.role, sign: t.sign === '-' ? '-' : '+' }))
  return {
    op,
    target_role: target,
    terms: terms.length ? terms : defaultTerms(target, roles),
    rows_role: rowRoles.includes(draft.rows_role) ? draft.rows_role : (rowRoles[0] || ''),
    tolerance: Number(draft.tolerance || 0.01),
  }
}

function previewArithDraft(draft) {
  if (!draft?.target_role) return ''
  if (draft.op === 'sumrows') return `${draft.target_role} = somma ${roleLabel(draft.rows_role)}`
  return `${draft.target_role} = ${formatSignedTerms(draft.terms)}`
}

function describeRules(extra) {
  const lines = []
  const modeLine = extractDescription(extra?.extract)
  if (modeLine) lines.push(`Estrazione: ${modeLine}.`)
  for (const rule of (extra?.rules || [])) {
    const text = formatRule(rule)
    if (rule.relation === 'target') lines.push(`Controllo: ${text}.`)
    else if (rule.relation === 'rows') lines.push(`Alimenta una somma: ${text}.`)
    else lines.push(`Partecipa al controllo: ${text}.`)
  }
  return lines.length ? lines.join(' ') : 'Nessuna regola dichiarata.'
}

function describeEmpty(field, eff, extract) {
  const form = cleanText(eff?.semantic_form) || cleanText(field?.semantic_form)
  if (OPTIONAL_EMPTY_FORMS.has(form)) return 'Se il valore manca nel perimetro, il campo resta vuoto risolto.'
  if (extract?.mode === 'colonne_a_vuoti') return 'Se la colonna prevista e\' vuota, il campo resta vuoto.'
  return 'Se nel perimetro non c\'e\' un valore conforme, B lascia il campo vuoto.'
}

function buildFieldCard(field, eff, extra) {
  if (!field) return null
  const anchors = cardAnchors(eff, extra)
  const form = describeForm(field, eff)
  const formBadge = form.source === 'testata_inherited' ? '(ereditata)' : (form.source === 'confermata' ? '(confermata)' : '(inferita)')
  const effectiveForm = cleanText(eff?.semantic_form) || cleanText(field?.semantic_form)
  const dateConvention = cleanText(eff?.date_convention) || cleanText(field?.date_convention) || (effectiveForm === 'data' ? 'it_dmy' : '')
  const inheritedFrom = inheritedTestataFieldName(eff, field, extra?.field)
  const barcodeSource = (extra?.barcode_sources || [])[0]
  const rows = [
    { key: 'Campo', value: `${fieldLabel(field)} (${fieldIdent(field)})` },
    { key: 'Ancora', value: describeAnchors(anchors) },
    { key: 'Perimetro', value: describePerimeter(eff, anchors, extra?.extract) },
    { key: "Direzione", value: describeLabelDirection(eff?.label_direction || field?.label_direction || extra?.field?.label_direction), badge: LABEL_DIRECTION_LABELS[normalizeLabelDirection(eff?.label_direction || field?.label_direction || extra?.field?.label_direction)] },
    { key: 'Forma', value: form.text, note: form.note, badge: formBadge },
    { key: 'Regole', value: describeRules(extra) },
    { key: 'Se vuoto', value: describeEmpty(field, eff, extra?.extract) },
  ]
  if (inheritedFrom) {
    rows.splice(2, 0, {
      key: 'Eredita',
      value: `Testata: ${inheritedFrom}`,
      badge: 'testata',
    })
  }
  if (effectiveForm === 'data') {
    rows.splice(5, 0, {
      key: 'Data',
      value: DATE_CONVENTION_LABELS[dateConvention] || dateConvention || 'IT giorno/mese/anno',
      badge: dateConvention || 'it_dmy',
    })
  }
  if (barcodeSource) {
    rows.splice(5, 0, {
      key: 'Barcode',
      value: cleanText(barcodeSource.token_text) || 'Nessun barcode decodificato in quest\'area.',
      badge: cleanText(barcodeSource.barcode_symbology) || 'barcode',
    })
  }
  if (extra?.error) rows.push({ key: 'Extra', value: 'Lettura dettagli non disponibile in questa sessione.' })
  return { title: fieldLabel(field), code: fieldIdent(field), rows }
}

function toMarkedRegion(r) {
  return {
    region_id: r.region_id,
    profile_id: r.profile_id,
    field_name: r.field_name,
    x0: Number(r.x0), y0: Number(r.y0), x1: Number(r.x1), y1: Number(r.y1),
    page_no: Number(r.page_no || 1),
    region_kind: r.region_kind, token_text: r.token_text,
    barcode_symbology: r.barcode_symbology,
  }
}

function mergeRegions(groups) {
  const out = []
  const seen = new Set()
  for (const r of groups.flat()) {
    if (!r) continue
    if (!r.field_name && r.region_kind !== 'table_area') continue  // table_area non ha field_name
    const m = toMarkedRegion(r)
    if (![m.x0, m.y0, m.x1, m.y1].every(Number.isFinite)) continue
    const key = [m.field_name, m.region_kind || 'value', m.x0, m.y0, m.x1, m.y1, m.token_text || ''].join(':')
    if (seen.has(key)) continue
    seen.add(key)
    out.push(m)
  }
  return out
}

// Marcatura TESTATA (valore/ancora) + RIGHE (area-tabella/colonna, DEC-0046 Fase A).
export default function App() {
  const scale = RENDER_DPI / 72
  const [classes, setClasses] = useState([])
  const [classe, setClasse] = useState('')
  const [fields, setFields] = useState([])
  const [effective, setEffective] = useState([])
  const [docnumber, setDocnumber] = useState('')
  const [author, setAuthor] = useState('operatore')
  const [apiKeyDraft, setApiKeyDraft] = useState(() => getApiKey())
  const [profile, setProfile] = useState(null)   // {profile_id, version}
  const [wl, setWl] = useState(null)              // wordlayer
  const [pageUrl, setPageUrl] = useState(null)
  const [marked, setMarked] = useState([])
  const [selectedField, setSelectedField] = useState(null)
  const [markMode, setMarkMode] = useState('value')   // 'value' | 'anchor' | 'table_area' | 'column' | 'footer'
  const [anchorRole, setAnchorRole] = useState('start') // 'start' (etichetta) | 'end' (fine, per il tra-ancore)
  const [labelDirection, setLabelDirection] = useState("") // direzione prossimita label: omni/right/left/up/down
  const [edgeScope, setEdgeScope] = useState('page')     // bordo verticale: 'page' | 'doc'
  const [suggested, setSuggested] = useState(null)   // {field_name, text} ancora suggerita (fase B)
  const [lastWord, setLastWord] = useState(null)   // per lo shift-clic (span multi-parola)
  const [footerSel, setFooterSel] = useState([])   // Piede: parole selezionate a toggle (multi-clic)
  const [showDone, setShowDone] = useState(true)   // mostra/nasconde le coppie gia' fatte
  const [showAllMarks, setShowAllMarks] = useState(() => {
    try { return localStorage.getItem('ttai_onboarding_showallmarks') === '1' } catch { return false }
  })
  useEffect(() => { try { localStorage.setItem('ttai_onboarding_showallmarks', showAllMarks ? '1' : '0') } catch {} }, [showAllMarks])
  const [onlyTestata, setOnlyTestata] = useState(true)  // nasconde i campi tabella/riga (euristica)
  const [worklists, setWorklists] = useState(() => loadWorklists())  // liste campioni per classe
  const [wlIdx, setWlIdx] = useState(-1)
  const [wlAdd, setWlAdd] = useState('')
  const [msg, setMsg] = useState('')
  const [busy, setBusy] = useState(false)
  const [longOp, setLongOp] = useState(null)       // operazione lunga in corso: OCR, barcode, Board, forme
  const [longOpElapsed, setLongOpElapsed] = useState(0)
  const [longOpSlow, setLongOpSlow] = useState(false)
  const [showForms, setShowForms] = useState(false)   // modale conferma tipi (DEC-0050)
  const [formsCat, setFormsCat] = useState([])        // vocabolario chiuso delle forme
  const [formsAreaOrder, setFormsAreaOrder] = useState([])
  const [formsAreaLabels, setFormsAreaLabels] = useState({})
  const [formEdits, setFormEdits] = useState({})      // field_name -> forma scelta nella modale
  const [anchorsLocal, setAnchorsLocal] = useState({}) // ancore impostate in sessione (field_name -> testo)
  const [fieldExtras, setFieldExtras] = useState({})    // classe::field_name -> dettagli read-only carta
  const [fieldExtraLoadingKey, setFieldExtraLoadingKey] = useState('')
  const [arithData, setArithData] = useState(null)       // ruoli e regole aritmetiche della classe
  const [arithDraft, setArithDraft] = useState(EMPTY_ARITH_DRAFT)
  const [arithLoading, setArithLoading] = useState(false)
  const [arithError, setArithError] = useState('')
  const [arithConflict, setArithConflict] = useState(null)
  const [externalChecks, setExternalChecks] = useState({}) // classe -> esito DEC-0108
  const [declaredClasses, setDeclaredClasses] = useState({}) // classe -> dichiarata per scrittura
  const [gapColumns, setGapColumns] = useState([])
  const [gapSelected, setGapSelected] = useState(0)
  const [gapExpected, setGapExpected] = useState(0)
  const [gapSource, setGapSource] = useState('')
  const [listPreview, setListPreview] = useState(null)
  const [listFormDraft, setListFormDraft] = useState('')
  const [panelW, setPanelW] = useState(() => {         // larghezza pannello destro (ridimensionabile)
    try { const s = Number(window.localStorage.getItem(PANEL_W_KEY)); if (s >= 280 && s <= 900) return s } catch { /* ignore */ }
    return 340
  })
  const [panelH, setPanelH] = useState(() => {         // altezza pannello quando ancorato sotto
    try { const s = Number(window.localStorage.getItem(PANEL_H_KEY)); if (s >= 160 && s <= 700) return s } catch { /* ignore */ }
    return 280
  })
  const [dock, setDock] = useState(() => {             // 'right' (affiancato) | 'bottom' (sotto), stile DevTools
    try { const s = window.localStorage.getItem(DOCK_KEY); if (s === 'right' || s === 'bottom') return s } catch { /* ignore */ }
    return 'right'
  })
  function chooseDock(d) { setDock(d); try { window.localStorage.setItem(DOCK_KEY, d) } catch { /* ignore */ } }
  const [theme, setTheme] = useState(() => {
    try { const s = window.localStorage.getItem(THEME_KEY); if (s === 'light' || s === 'dark') return s } catch { /* ignore */ }
    return 'light'
  })
  function chooseTheme(t) { setTheme(t); try { window.localStorage.setItem(THEME_KEY, t) } catch { /* ignore */ } }
  useEffect(() => { try { document.documentElement.setAttribute('data-theme', theme) } catch { /* ignore */ } }, [theme])
  useEffect(() => {
    if (!longOp) return undefined
    function tick() {
      const elapsed = Math.floor((Date.now() - longOp.startedAt) / 1000)
      setLongOpElapsed(elapsed)
      setLongOpSlow(elapsed >= 20)
    }
    tick()
    const timer = window.setInterval(tick, 1000)
    return () => window.clearInterval(timer)
  }, [longOp?.startedAt])
  function beginLongOp(key, label, slowText) {
    setBusy(true)
    setLongOp({ key, label, slowText, startedAt: Date.now() })
    setLongOpElapsed(0)
    setLongOpSlow(false)
  }
  function finishLongOp() {
    setLongOp(null)
    setLongOpElapsed(0)
    setLongOpSlow(false)
    setBusy(false)
  }
  function userError(e) {
    const detail = e?.data?.detail || e?.message || String(e || 'errore non specificato')
    return String(detail)
      .replace(/(X-Ttai-Api-Key|api[_-]?key|token)=([^&\s]+)/gi, '$1=***')
      .replace(/(Bearer\s+)[A-Za-z0-9._~+/=-]+/gi, '$1***')
  }
  function isPendingWordlayer(layer) {
    const status = String(layer?.status || '').toLowerCase()
    return !Array.isArray(layer?.pages) && ['pending', 'running', 'processing', 'retry', 'queued'].includes(status)
  }
  function waitMs(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms))
  }
  async function waitForWordlayer(doc, firstStatus) {
    let current = firstStatus
    for (let attempt = 0; attempt < 240; attempt += 1) {
      const status = String(current?.status || current?.db_status || 'pending').toLowerCase()
      setMsg(`Documento ${doc}: OCR in corso (${status}). Attendo la cache word-layer...`)
      const pollMs = Math.max(1000, Math.min(Number(current?.poll_after_ms || 2500), 10000))
      await waitMs(pollMs)
      current = await api.wordlayerStatus(Number(doc))
      if (!isPendingWordlayer(current)) return current
    }
    throw new Error(`OCR word-layer non completato per ${doc}: timeout polling`)
  }
  const [zoneGrow, setZoneGrow] = useState(() => {
    try { const s = JSON.parse(window.localStorage.getItem(ZONE_KEY) || 'null'); if (Array.isArray(s) && s.length === 3) return s } catch { /* ignore */ }
    return [1, 1, 1]
  })
  function startZoneResize(e, ia, ib) {         // trascina il divisore tra due zone (ancoraggio sotto)
    e.preventDefault()
    const row = e.currentTarget.parentElement
    const width = (row && row.clientWidth) || 900
    const g = zoneGrow.slice()
    const total = g[0] + g[1] + g[2]
    const startX = e.clientX
    const a = g[ia], b = g[ib]
    let last = g
    function move(ev) {
      const frac = ((ev.clientX - startX) / width) * total
      const ng = g.slice()
      ng[ia] = Math.max(0.3, a + frac)
      ng[ib] = Math.max(0.3, b - frac)
      last = ng; setZoneGrow(ng)
    }
    function up() {
      document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up)
      document.body.style.cursor = ''
      try { window.localStorage.setItem(ZONE_KEY, JSON.stringify(last)) } catch { /* ignore */ }
    }
    document.body.style.cursor = 'col-resize'
    document.addEventListener('mousemove', move); document.addEventListener('mouseup', up)
  }

  const currentClass = useMemo(
    () => classes.find((c) => c.classe === classe) || null, [classes, classe])
  const currentClassActive = isProfileActive(currentClass)
  const classIsAzure = !!(currentClass && (currentClass.azure_write_forbidden ?? currentClass.has_azure))
  const needsDeclaration = !!currentClass && (!currentClassActive || classIsAzure)
  const externalCheck = classe ? externalChecks[classe] : null
  const externalGateChip = externalChip(externalCheck)
  const classDeclared = !!(classe && declaredClasses[classe])
  const classCanProceed = !!currentClass && (!needsDeclaration || classDeclared)
  // Worklist della classe corrente (fallback __default__ se la classe non ha una lista propria).
  const worklist = useMemo(
    () => worklists[classe] ?? worklists.__default__ ?? [], [worklists, classe])
  // Contenitore-tabella (prefisso CLASSE... dei campi-riga): a lui appende il piede (DEC-0056).
  const tableContainer = useMemo(() => {
    const conts = []
    for (const f of fields) {
      const n = String(f.field_name || ''); const i = n.indexOf('.')
      if (/^CLASSE/i.test(n) && i > 0) { const c = n.slice(0, i); if (!conts.includes(c)) conts.push(c) }
    }
    return conts[0] || null
  }, [fields])
  const page1 = wl && wl.pages && wl.pages[0]
  const page1Barcodes = useMemo(() => {
    const pages = wl?.barcodes?.pages || []
    const found = pages.find((p) => Number(p.page_no || 1) === 1) || pages[0]
    return found?.barcodes || []
  }, [wl])
  const activeProfileId = useMemo(() => {
    const row = effective.find((r) => r.profile_id)
    return row ? Number(row.profile_id) : null
  }, [effective])
  const markedDoneFields = useMemo(
    () => new Set(marked.filter((r) => r.field_name).map((r) => r.field_name)), [marked])
  const markableFields = useMemo(() => fields.filter((f) => !isSystemField(f)), [fields])
  const selectedFieldInfo = useMemo(
    () => markableFields.find((f) => f.field_name === selectedField) || null, [markableFields, selectedField])
  const selectedEffective = useMemo(
    () => effective.find((r) => r.field_name === selectedField) || null, [effective, selectedField])
  const selectedExtraKey = classe && selectedField ? `${classe}::${selectedField}` : ''
  const selectedExtra = selectedExtraKey ? fieldExtras[selectedExtraKey] : null
  const fieldExtraLoading = !!selectedExtraKey && fieldExtraLoadingKey === selectedExtraKey
  const selectedFieldCanInherit = !!selectedFieldInfo && isRigaField(selectedFieldInfo.field_name)
  const selectedInheritSource = inheritedTestataFieldName(selectedEffective, selectedFieldInfo, selectedExtra?.field)
  const testataInheritOptions = useMemo(
    () => markableFields.filter((f) => !isRigaField(f.field_name)),
    [markableFields])
  const selectedFieldForm = cleanText(selectedEffective?.semantic_form) || cleanText(selectedFieldInfo?.semantic_form)
  const selectedFieldIsDate = selectedFieldForm === 'data'
  const selectedDateConvention = cleanText(selectedEffective?.date_convention)
    || cleanText(selectedFieldInfo?.date_convention)
    || (selectedFieldIsDate ? 'it_dmy' : '')
  const selectedLabelDirection = normalizeLabelDirection(
    cleanText(selectedEffective?.label_direction)
      || cleanText(selectedFieldInfo?.label_direction)
      || cleanText(selectedExtra?.field?.label_direction)
  )
  useEffect(() => { setLabelDirection(selectedLabelDirection || "") }, [selectedField, selectedLabelDirection])

  const identityCard = useMemo(
    () => buildFieldCard(selectedFieldInfo, selectedEffective, selectedExtra),
    [selectedFieldInfo, selectedEffective, selectedExtra])
  const formGroups = useMemo(
    () => buildFormGroups(formsCat, formsAreaOrder, formsAreaLabels),
    [formsCat, formsAreaOrder, formsAreaLabels])
  const selectedFieldIsMultiWord = useMemo(() => {
    if (!selectedFieldInfo) return false
    const form = normText(selectedFieldInfo.semantic_form)
    const label = normText(`${selectedFieldInfo.field_name} ${selectedFieldInfo.arxivar_label || ''}`)
    return MULTI_WORD_FORMS.has(form) || /ragione|indirizzo|descrizione/.test(label)
  }, [selectedFieldInfo])
  useEffect(() => {
    setListFormDraft(selectedFieldForm || 'testo_generico')
    setListPreview(null)
  }, [selectedField, selectedFieldForm])
  useEffect(() => {
    if (markMode !== 'list' || formsCat.length) return
    let alive = true
    api.formsCatalog()
      .then((c) => {
        if (!alive) return
        setFormsCat(c.forms || [])
        setFormsAreaOrder(c.area_order || [])
        setFormsAreaLabels(c.area_labels || {})
      })
      .catch((e) => { if (alive) setMsg(String(e)) })
    return () => { alive = false }
  }, [markMode, formsCat.length])
  const isDone = (f) => {
    const eff = effective.find((r) => r.field_name === f.field_name)
    return !!(eff && eff.n_regions > 0) || markedDoneFields.has(f.field_name)
  }
  const doneCount = markableFields.filter(isDone).length
  const rowFieldCount = useMemo(() => markableFields.filter((f) => isRigaField(f.field_name)).length, [markableFields])
  const displayFieldGroups = useMemo(
    () => buildFieldListGroups(markableFields, { showDone, onlyTestata, isDone }),
    [markableFields, showDone, onlyTestata, effective, markedDoneFields])
  const hasRowFields = rowFieldCount > 0
  const activeMode = MODE_META[markMode] || MODE_META.value
  const activeFamily = activeMode.family
  const canvasWordsActive = markMode === 'footer' || (!!selectedField && !PASSIVE_MODES.has(markMode) && markMode !== 'gap_columns' && markMode !== 'barcode' && markMode !== 'area')
  const intentHighlight = useMemo(() => {
    if (!marked.length) return []
    if (showAllMarks) return marked
    if (markMode === 'table_area') return marked.filter((m) => m.region_kind === 'table_area')
    if (markMode === 'column') return marked.filter((m) => m.region_kind === 'column' && (!selectedField || m.field_name === selectedField))
    if (markMode === 'list') return selectedField
      ? marked.filter((m) => m.region_kind === 'list' && m.field_name === selectedField)
      : marked.filter((m) => m.region_kind === 'list')
    if (markMode === 'value') return selectedField
      ? marked.filter((m) => m.field_name === selectedField && (m.region_kind || 'value') === 'value')
      : []
    if (markMode === 'area') return selectedField
      ? marked.filter((m) => m.field_name === selectedField && m.region_kind === 'area')
      : []
    if (markMode === 'barcode') return selectedField
      ? marked.filter((m) => m.field_name === selectedField && m.region_kind === 'barcode')
      : []
    if (markMode === 'summary') return selectedField
      ? marked.filter((m) => m.field_name === selectedField)
      : marked.filter((m) => m.region_kind === 'table_area')
    return []
  }, [marked, markMode, selectedField, showAllMarks])
  const gapColumnOverlays = useMemo(() => (
    markMode === 'gap_columns'
      ? gapColumns.map((c) => ({ ...c, selected: Number(c.index) === Number(gapSelected) }))
      : []
  ), [markMode, gapColumns, gapSelected])
  // Click sul nome campo: centra prima la regione-valore, poi qualunque area marcata del campo.
  useEffect(() => {
    if (!selectedField || !marked.length || !page1) return
    const reg = marked.find((m) => m.field_name === selectedField && (m.region_kind || 'value') === 'value')
      || marked.find((m) => m.field_name === selectedField)
    if (!reg || !page1.width || !page1.height) return
    const t = setTimeout(() => {
      try {
        const col = document.querySelector('.canvas-col')
        const wrap = document.querySelector('.canvas-col .page-wrap')
        if (!col || !wrap) return
        const yc = ((reg.y0 + reg.y1) / 2) / page1.height * wrap.offsetHeight
        const xc = ((reg.x0 + reg.x1) / 2) / page1.width * wrap.offsetWidth
        col.scrollTo({ top: Math.max(0, yc - col.clientHeight / 2), left: Math.max(0, xc - col.clientWidth / 2), behavior: 'smooth' })
      } catch { /* ignore */ }
    }, 40)
    return () => clearTimeout(t)
  }, [selectedField, marked, page1])
  // Ancora impostata per un campo: sessione corrente (anchorsLocal) o Board effettiva (v_class_profile_effective).
  function fieldAnchor(fn) {
    if (anchorsLocal[fn]) return anchorsLocal[fn]
    const eff = effective.find((r) => r.field_name === fn)
    if (eff && Number(eff.n_anchors || 0) > 0) return eff.anchors || '(impostata)'
    return ''
  }
  const foreignRegionCount = useMemo(() => (
    profile ? marked.filter((r) => r.profile_id && Number(r.profile_id) !== Number(profile.profile_id)).length : 0
  ), [marked, profile])

  useEffect(() => {
    if (!selectedExtraKey || fieldExtras[selectedExtraKey]) return
    let alive = true
    setFieldExtraLoadingKey(selectedExtraKey)
    api.fieldExtras(classe, selectedField)
      .then((data) => {
        if (!alive) return
        setFieldExtras((m) => ({ ...m, [selectedExtraKey]: data }))
      })
      .catch((err) => {
        if (!alive) return
        setFieldExtras((m) => ({ ...m, [selectedExtraKey]: { error: String(err) } }))
      })
      .finally(() => { if (alive) setFieldExtraLoadingKey('') })
    return () => { alive = false }
  }, [classe, selectedField, selectedExtraKey, fieldExtras])

  async function loadArithRules({ silent = false } = {}) {
    if (!classe) return null
    setArithLoading(true)
    try {
      const data = await api.arithRules(classe)
      setArithData(data)
      setArithDraft((d) => normalizeArithDraft(d, data))
      if (!silent) setMsg(`Regole: ${(data.rules || []).length} dichiarate`)
      return data
    } catch (e) {
      if (!silent) setMsg(String(e))
      throw e
    } finally {
      setArithLoading(false)
    }
  }

  useEffect(() => {
    if (markMode !== 'rules' || !classe) return
    let alive = true
    setArithLoading(true)
    api.arithRules(classe)
      .then((data) => {
        if (!alive) return
        setArithData(data)
        setArithDraft((d) => normalizeArithDraft(d, data))
      })
      .catch((err) => { if (alive) setMsg(String(err)) })
      .finally(() => { if (alive) setArithLoading(false) })
    return () => { alive = false }
  }, [markMode, classe])

  function setRuleOp(op) {
    setArithError('')
    setArithConflict(null)
    setArithDraft((d) => normalizeArithDraft({ ...d, op }, arithData))
  }

  function setRuleTarget(target) {
    const roles = roleValues(arithData)
    setArithError('')
    setArithConflict(null)
    setArithDraft((d) => ({ ...d, target_role: target, terms: defaultTerms(target, roles) }))
  }

  function updateTerm(idx, patch) {
    setArithError('')
    setArithConflict(null)
    setArithDraft((d) => {
      const terms = (d.terms || []).map((t, i) => i === idx ? { ...t, ...patch } : t)
      return { ...d, terms }
    })
  }

  function addRuleTerm() {
    const roles = roleValues(arithData)
    setArithError('')
    setArithConflict(null)
    setArithDraft((d) => {
      const used = new Set((d.terms || []).map((t) => t.role))
      const role = roles.find((r) => r !== d.target_role && !used.has(r)) || roles.find((r) => r !== d.target_role) || roles[0] || ''
      return role ? { ...d, terms: [...(d.terms || []), { role, sign: '+' }] } : d
    })
  }

  function removeRuleTerm(idx) {
    setArithError('')
    setArithConflict(null)
    setArithDraft((d) => ({ ...d, terms: (d.terms || []).filter((_, i) => i !== idx) }))
  }

  async function saveArithRule() {
    if (!classe) return
    const draft = normalizeArithDraft(arithDraft, arithData)
    if (!draft.target_role) { setMsg('Regole: nessun ruolo target disponibile.'); return }
    if (draft.op === 'signed_sum' && !draft.terms.length) { setMsg('Regole: aggiungi almeno un termine.'); return }
    if (draft.op === 'sumrows' && !draft.rows_role) { setMsg('Regole: nessun ruolo-riga disponibile.'); return }
    setArithError('')
    setArithConflict(null)
    setBusy(true)
    try {
      await api.saveArithRule(classe, draft)
      setFieldExtras({})
      await loadArithRules({ silent: true })
      setMsg(`Regola dichiarata: ${previewArithDraft(draft)}`)
    } catch (e) {
      const message = arithErrorMessage(e)
      const duplicate = arithProblems(e).find((p) => p.type === 'duplicate_target')
      setArithError(message)
      setArithConflict(duplicate ? { target: duplicate.target, draft, message } : null)
      setMsg(message)
    } finally {
      setBusy(false)
    }
  }

  async function replaceArithRule() {
    if (!classe || !arithConflict?.target || !arithConflict?.draft) return
    const existing = (arithData?.rules || [])
      .find((r) => r.enabled !== false && r.target_role === arithConflict.target)
    if (!existing?.rule_id) {
      setArithError('Regola esistente non trovata: aggiorna e riprova.')
      return
    }
    setBusy(true)
    try {
      await api.disableArithRule(classe, existing.rule_id)
      await api.saveArithRule(classe, arithConflict.draft)
      setArithError('')
      setArithConflict(null)
      setFieldExtras({})
      await loadArithRules({ silent: true })
      setMsg(`Regola sostituita: ${previewArithDraft(arithConflict.draft)}`)
    } catch (e) {
      const message = arithErrorMessage(e)
      setArithError(message)
      setMsg(message)
    } finally {
      setBusy(false)
    }
  }

  async function disableArithRule(rule) {
    if (!classe || !rule?.rule_id) return
    setBusy(true)
    try {
      await api.disableArithRule(classe, rule.rule_id)
      setArithError('')
      setArithConflict(null)
      setFieldExtras({})
      await loadArithRules({ silent: true })
      setMsg(`Regola rimossa: ${formatRule(rule)}`)
    } catch (e) {
      setMsg(String(e))
    } finally {
      setBusy(false)
    }
  }

  function switchMode(nextMode) {
    setMarkMode(nextMode)
    setLastWord(null)
    if (nextMode !== 'footer') setFooterSel([])
    if (nextMode !== 'anchor') setSuggested(null)
    if (nextMode !== 'gap_columns') resetGapTool()
    if (nextMode !== 'list') setListPreview(null)
    if (MODE_META[nextMode]?.family === 'table') setOnlyTestata(false)
  }

  function resetGapTool() {
    setGapColumns([])
    setGapSelected(0)
    setGapExpected(0)
    setGapSource('')
  }

  function openExplainer(hash) {
    window.open(`./explainer.html#${hash}`, '_blank', 'noopener,noreferrer')
  }

  function selectFamily(family) {
    if (family === 'fields') return switchMode('value')
    if (family === 'anchors') return switchMode('anchor')
    if (family === 'table') return switchMode(markMode === 'column' || markMode === 'footer' || markMode === 'list' ? markMode : 'table_area')
    if (family === 'forms') { switchMode('forms'); if (classe) openForms(); return }
    if (family === 'rules') { switchMode('rules'); return }
    if (family === 'summary') return switchMode('summary')
  }

  async function connect() {
    setBusy(true); setMsg('')
    try {
      setApiKey(apiKeyDraft)
      const d = await api.classes()
      const rows = d.classes || []
      setClasses(rows)
      setMsg(`Backend onboarding raggiunto: ${rows.length} classi`)
      const deepLinkClasse = new URLSearchParams(window.location.search).get('classe')
      if (!classe && deepLinkClasse && rows.some((c) => c.classe === deepLinkClasse)) await selectClass(deepLinkClasse)
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }

  async function runExternalIdCheck(targetClasse, { declare = false, silent = false } = {}) {
    if (!targetClasse) return null
    setExternalChecks((m) => ({ ...m, [targetClasse]: { ...(m[targetClasse] || {}), loading: true, error: '' } }))
    try {
      const data = await api.externalIdCheck(targetClasse)
      setExternalChecks((m) => ({ ...m, [targetClasse]: { ...data, loading: false } }))
      if (declare) {
        setDeclaredClasses((m) => {
          const next = { ...m }
          if (data.ok) next[targetClasse] = true
          else delete next[targetClasse]
          return next
        })
      }
      if (!silent) {
        if (data.ok) setMsg(declare ? `Classe dichiarata: ${targetClasse}` : `External_id completo per ${targetClasse}`)
        else if (data.child_schema_missing) setMsg(`Dichiarazione bloccata: schema ClasseBox figlia non pronto per ${targetClasse}`)
        else setMsg(`Dichiarazione bloccata: ${data.n_missing || 0} external_id mancanti per ${targetClasse}`)
      }
      return data
    } catch (e) {
      const error = userError(e)
      setExternalChecks((m) => ({ ...m, [targetClasse]: { ok: false, n_missing: 0, missing: [], loading: false, error } }))
      if (declare) setDeclaredClasses((m) => { const next = { ...m }; delete next[targetClasse]; return next })
      if (!silent) setMsg(error)
      return null
    }
  }

  async function selectClass(cl) {
    setClasse(cl); setProfile(null); setWl(null); setPageUrl(null); setMarked([]); setSelectedField(null); setLastWord(null); setSuggested(null); setAnchorsLocal({}); setFieldExtras({}); setFieldExtraLoadingKey(''); setArithData(null); setArithDraft(EMPTY_ARITH_DRAFT); setArithError(''); setArithConflict(null); resetGapTool(); setWlIdx(-1)
    if (!cl) return
    setExternalChecks((m) => ({ ...m, [cl]: { ...(m[cl] || {}), loading: true, error: '' } }))
    beginLongOp('forms', 'Sincronizzo la classe...', 'Sto rileggendo la struttura ARXivar e preparando campi e forme della classe. Puoi attendere: l\'operazione non e\' stata interrotta.')
    try {
      const sync = await api.resyncCatalog(cl, { created_by: author || 'onboarding-ui' })
      await api.ensureInferredForms(cl)
      const [f, e, x] = await Promise.all([
        api.fields(cl),
        api.effective(cl),
        api.externalIdCheck(cl).catch((err) => ({ error: String(err) })),
      ])
      setFields(f.fields || []); setEffective(e.rows || [])
      setExternalChecks((m) => ({
        ...m,
        [cl]: x.error ? { ok: false, n_missing: 0, missing: [], loading: false, error: x.error } : { ...x, loading: false },
      }))
      const added = Number(sync?.fields_added || 0)
      const childSchemaAdded = Number(sync?.inserted?.child_schema || 0)
      if (added > 0) {
        setMsg(`${added} nuovi campi rilevati e aggiunti${childSchemaAdded > 0 ? `; schema righe +${childSchemaAdded}` : ''}.`)
      }
    } catch (e) {
      const error = userError(e)
      setExternalChecks((m) => ({ ...m, [cl]: { ok: false, n_missing: 0, missing: [], loading: false, error } }))
      setMsg(error)
    } finally {
      finishLongOp()
    }
  }

  async function declareClass() {
    if (!classe) return
    setBusy(true)
    beginLongOp('declare-class', 'Dichiaro la classe...', 'Sto generando il catalogo campi e adottando la classe per l\'onboarding deterministico.')
    try {
      const out = await api.declareClass(classe, { created_by: author || 'onboarding-ui' })
      const [d, f, e, x] = await Promise.all([
        api.classes(),
        api.fields(classe),
        api.effective(classe),
        api.externalIdCheck(classe).catch((err) => ({ error: String(err) })),
      ])
      setClasses(d.classes || [])
      setFields(f.fields || [])
      setEffective(e.rows || [])
      const check = x.error ? { ok: false, n_missing: 0, missing: [], loading: false, error: x.error } : { ...x, loading: false }
      setExternalChecks((m) => ({ ...m, [classe]: check }))
      setDeclaredClasses((m) => {
        const next = { ...m }
        if (check.ok) next[classe] = true
        else delete next[classe]
        return next
      })
      const total = Number(out?.catalog?.fields_after ?? (f.fields || []).length)
      const missing = Number(check.n_missing || 0)
      if (check.ok) setMsg(`Classe dichiarata: ${classe}. Catalogo campi: ${total}; external_id completo.`)
      else if (check.catalog_missing) setMsg(`Dichiarazione incompleta: catalogo campi non generato per ${classe}.`)
      else if (check.child_schema_missing) setMsg(`Classe preparata: ${classe}. Catalogo campi: ${total}; schema ClasseBox figlia non pronto.`)
      else setMsg(`Classe preparata: ${classe}. Catalogo campi: ${total}; external_id mancanti: ${missing}.`)
    } catch (e) {
      setMsg(`Dichiara classe fallito: ${userError(e)}`)
    } finally {
      finishLongOp()
      setBusy(false)
    }
  }

  async function recheckExternalIds() {
    if (!classe) return
    setBusy(true)
    beginLongOp('resync-class', 'Ri-sincronizzo la classe...', 'Sto rileggendo la struttura ARXivar e aggiungo solo i campi mancanti, poi verifico gli external_id.')
    try {
      const out = await api.resyncCatalog(classe, { created_by: author || 'onboarding-ui' })
      await api.ensureInferredForms(classe)
      const [d, f, e] = await Promise.all([
        api.classes(),
        api.fields(classe),
        api.effective(classe),
      ])
      setClasses(d.classes || [])
      setFields(f.fields || [])
      setEffective(e.rows || [])
      const check = await runExternalIdCheck(classe, { declare: true, silent: true })
      const total = Number((f.fields || []).length)
      const added = Number(out?.fields_added || 0)
      const childSchemaAdded = Number(out?.inserted?.child_schema || 0)
      const missing = Number(check?.n_missing || 0)
      const addText = added > 0 ? `; nuovi campi: ${added}` : '; nessun nuovo campo'
      const schemaText = childSchemaAdded > 0 ? `; schema righe +${childSchemaAdded}` : ''
      if (check?.ok) setMsg(`Classe ri-sincronizzata: ${classe}. Catalogo campi: ${total}${addText}${schemaText}; external_id completo.`)
      else if (check?.child_schema_missing) setMsg(`Classe ri-sincronizzata: ${classe}. Catalogo campi: ${total}${addText}${schemaText}; schema ClasseBox figlia non pronto.`)
      else setMsg(`Classe ri-sincronizzata: ${classe}. Catalogo campi: ${total}${addText}${schemaText}; external_id mancanti: ${missing}.`)
    } catch (e) {
      setMsg(`Ri-sincronizza e verifica fallito: ${userError(e)}`)
    } finally {
      finishLongOp()
      setBusy(false)
    }
  }

  async function loadSample(docArg, opts = {}) {
    const doc = String((docArg ?? docnumber) || '').replace(/\D/g, '')
    if (!classe || !currentClass || !doc) { setMsg('Scegli classe e docnumber'); return }
    if (!classCanProceed) { setMsg('Dichiara la classe prima di procedere con l\'onboarding di scrittura.'); return }
    const forceOcr = !!opts.forceOcr
    beginLongOp(
      forceOcr ? 'ocr-force' : 'ocr',
      forceOcr ? 'OCR in corso (forzato)...' : 'OCR in corso...',
      'OCR ancora in corso. Per documenti pesanti puo\' richiedere piu\' tempo: attendi il completamento.'
    )
    setMsg('')
    try {
      // GUARD strict: il documento deve appartenere ALLA CLASSE SELEZIONATA, altrimenti non si apre.
      const dc = await api.documentClass(Number(doc))
      if (!dc || !dc.found || !dc.classe) {
        setMsg(`Documento ${doc}: classe non risolvibile in ARXivar. Non apro.`); return
      }
      if (dc.classe !== classe) {
        setMsg(`Il documento ${doc} appartiene alla classe "${dc.classe}", non a "${classe}". Non apro.`); return
      }
      setDocnumber(doc); setSelectedField(null); setLastWord(null); setSuggested(null); resetGapTool()
      const p = await api.upsertProfile({
        classe, documenttype: currentClass.documenttype,
        tipo2: currentClass.tipo2 || 0, tipo3: currentClass.tipo3 || 0, created_by: author,
      })
      setProfile(p)
      await api.addSample({ profile_id: p.profile_id, docnumber: Number(doc), added_by: author, source: 'mongo' })
      let layer = await api.wordlayer(Number(doc), { forceOcr })
      if (isPendingWordlayer(layer)) {
        layer = await waitForWordlayer(doc, layer)
      }
      setWl(layer)
      const url = await api.pageObjectUrl(Number(doc), 1, RENDER_DPI)
      setPageUrl(url)
      const regionProfileIds = [...new Set([p.profile_id, activeProfileId].filter(Boolean))]
      const loadedRegions = await Promise.all(
        regionProfileIds.map((pid) => api.regions(Number(doc), pid).catch(() => ({ regions: [] })))
      )
      const prior = mergeRegions(loadedRegions.map((r) => r.regions || []))
      setMarked(prior)
      const barcodeCount = Number(layer.barcodes?.doc?.n_barcodes || 0)
      setMsg(`Documento ${doc}: ${layer.pages?.[0]?.n_words ?? 0} parole` +
             (forceOcr ? ' - OCR forzato' : '') +
             (barcodeCount ? ` - ${barcodeCount} barcode` : '') +
             (layer.barcodes?.error ? ` - barcode: ${layer.barcodes.error}` : '') +
             (prior.length ? ` - ${prior.length} regioni gia' salvate recuperate` : '') +
             (layer.doc?.needs_ocr ? ' (needs_ocr: PDF scansionato)' : ''))
    } catch (e) { setMsg(`Caricamento documento ${doc} fallito: ${userError(e)}`) } finally { finishLongOp() }
  }

  async function applyProfileToDocument() {
    const doc = String(docnumber || '').replace(/\D/g, '')
    if (!classe || !doc || !page1) { setMsg('Carica prima un documento.'); return }
    if (!classCanProceed) { setMsg('Dichiara la classe prima di riportare le regole.'); return }
    if (classIsAzure) { setMsg('Riporta regole disponibile solo per classi non-Azure.'); return }
    if (!profile?.profile_id) { setMsg('Crea o carica prima la bozza della Board.'); return }
    beginLongOp('apply-profile', 'Applico la Board...', 'Sto ancora riportando le regole nella bozza. Attendi il completamento prima di procedere.')
    try {
      const out = await api.applyProfile(Number(doc), classe, profile.profile_id)
      const regionProfileIds = [...new Set([out.target_profile_id || out.profile_id, profile?.profile_id, activeProfileId].filter(Boolean))]
      const loadedRegions = await Promise.all(
        regionProfileIds.map((pid) => api.regions(Number(doc), pid).catch(() => ({ regions: [] })))
      )
      setMarked(mergeRegions(loadedRegions.map((r) => r.regions || [])))
      setShowAllMarks(true)
      const listCount = out.lists ?? out.list_regions ?? 0
      const listMiss = out.list_not_located_count ?? 0
      setMsg(`Regole riportate nella bozza v${profile.version}: ${out.campi_aggiunti ?? out.marked ?? 0} aggiunti, ${out.campi_umani_mantenuti || 0} tuoi mantenuti, ${out.not_located_count || 0} non localizzati, ${out.columns || 0} colonne, ${out.table_area || 0} area tabella, ${out.rows || 0} righe, ${listCount} aree lista, ${listMiss} liste non riportate, ${out.areas || out.area_regions || 0} aree geografiche.`)
    } catch (e) {
      const detail = userError(e)
      setMsg(`Riporta regole fallito: ${detail}`)
    } finally {
      finishLongOp()
    }
  }

  async function consolidateDraft() {
    const doc = String(docnumber || '').replace(/\D/g, '')
    if (!classe || !doc || !profile?.profile_id) { setMsg('Carica prima documento e bozza della Board.'); return }
    setBusy(true)
    try {
      const out = await api.consolidateDraft(Number(doc), classe, profile.profile_id)
      const regionProfileIds = [...new Set([profile.profile_id, activeProfileId].filter(Boolean))]
      const loadedRegions = await Promise.all(
        regionProfileIds.map((pid) => api.regions(Number(doc), pid).catch(() => ({ regions: [] })))
      )
      setMarked(mergeRegions(loadedRegions.map((r) => r.regions || [])))
      setMsg(`Bozza consolidata: ${out.removed || 0} regioni auto residue rimosse. Ora puoi attivare.`)
    } catch (e) {
      setMsg(`Consolida bozza fallito: ${userError(e)}`)
    } finally {
      setBusy(false)
    }
  }

  async function refreshBarcode(force = true) {
    const doc = String(docnumber || '').replace(/\D/g, '')
    if (!doc || !page1) { setMsg('Carica prima un documento.'); return }
    beginLongOp('barcode', 'Decodifica barcode...', 'Decodifica barcode ancora in corso. Attendi il completamento prima di rilanciare.')
    try {
      const barcodes = await api.barcode(Number(doc), force)
      setWl((old) => old ? { ...old, barcodes } : old)
      const count = Number(barcodes?.doc?.n_barcodes || 0)
      setMsg(count ? `Barcode rilevati: ${count}` : 'Nessun barcode decodificato.')
    } catch (e) {
      setMsg(`Decodifica barcode fallita: ${userError(e)}`)
    } finally {
      finishLongOp()
    }
  }

  function loadFromWorklist(i) {
    if (i < 0 || i >= worklist.length) return
    setWlIdx(i)
    loadSample(String(worklist[i].doc))
  }
  // Scrive la worklist per la classe corrente (o nel bucket __default__ se nessuna classe selezionata).
  function updateClassWorklist(nextList) {
    const key = classe || '__default__'
    const next = { ...worklists, [key]: nextList }
    setWorklists(next); saveWorklists(next)
  }
  function addToWorklist() {
    const d = String(wlAdd || '').replace(/\D/g, '')
    if (!d || worklist.some((it) => String(it.doc) === d)) { setWlAdd(''); return }
    updateClassWorklist([...worklist, { doc: d, hint: '' }]); setWlAdd('')
  }
  function removeFromWorklist(i) {
    const next = worklist.filter((_, k) => k !== i)
    updateClassWorklist(next)
    if (wlIdx >= next.length) setWlIdx(-1)
  }
  // Carica i campioni PER CLASSE da un file .json (merge sulle classi presenti nel file).
  function importWorklistsFile(file) {
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const norm = normalizeWorklistJson(JSON.parse(String(reader.result || '')))
        const nCls = Object.keys(norm).length
        if (!nCls) { setMsg('JSON campioni: nessuna classe valida trovata.'); return }
        const next = { ...worklists, ...norm }
        setWorklists(next); saveWorklists(next); setWlIdx(-1)
        const nDoc = Object.values(norm).reduce((a, b) => a + b.length, 0)
        setMsg(`Campioni caricati da file: ${nCls} classi, ${nDoc} documenti` +
               (classe && !norm[classe] ? ` (la classe corrente ${classe} non e' nel file)` : ''))
      } catch (e) { setMsg('JSON campioni non valido: ' + e) }
    }
    reader.readAsText(file)
  }

  // parole (page1) i cui centri cadono nel box, ordinate per (y,x)
  function wordsInBox(box) {
    return (page1?.words || [])
      .filter((w) => {
        const cx = (w.x0 + w.x1) / 2, cy = (w.y0 + w.y1) / 2
        return cx >= box.x0 && cx <= box.x1 && cy >= box.y0 && cy <= box.y1
      })
      .sort((a, b) => (a.y0 - b.y0) || (a.x0 - b.x0))
  }
  function unionBox(ws) {
    return {
      x0: Math.min(...ws.map((w) => w.x0)), y0: Math.min(...ws.map((w) => w.y0)),
      x1: Math.max(...ws.map((w) => w.x1)), y1: Math.max(...ws.map((w) => w.y1)),
    }
  }

  function textOfWords(ws) {
    return wordsText(ws)
  }

  // UNA regione (prima->ultima parola), con testo concatenato -> H se c'e' testo
  async function saveRegion(box, text, source) {
    if (!selectedField || !profile) return
    const hasText = !!(text && text.trim())
    const valueText = hasText ? text.trim() : ''
    setBusy(true)
    try {
      await api.addRegion({
        profile_id: profile.profile_id, classe, docnumber: Number(docnumber), page_no: 1,
        x0: box.x0, y0: box.y0, x1: box.x1, y1: box.y1, created_by: author,
        field_name: selectedField, region_kind: 'value', unit: 'point',
        page_width: page1?.width, page_height: page1?.height,
        source, token_text: hasText ? valueText.slice(0, 400) : null, is_value_marked: hasText,
      })
      if (hasText) {
        await api.humanTruth({
          classe, docnumber: Number(docnumber), revision_id: 0, field_scope: 'testata',
          field_name: selectedField, h_value: valueText, h_author: author,
          h_reason: 'onboarding: marcatura testata',
        })
      }
      setMarked((m) => [...m, { field_name: selectedField, profile_id: profile.profile_id, x0: box.x0, y0: box.y0, x1: box.x1, y1: box.y1, region_kind: 'value', token_text: hasText ? valueText : null }])
      const e = await api.effective(classe); setEffective(e.rows || [])
      setLastWord(null)
      setMsg(`Marcato ${selectedField}${hasText ? ` = "${valueText}"` : ' (regione)'}`)
      // fase B: proponi l'ancora SOLO se il campo non ne ha gia' una (manuale o effettiva)
      const sug = suggestAnchor(box, page1?.words || [])
      setSuggested(sug && !fieldAnchor(selectedField) ? { field_name: selectedField, text: sug } : null)
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }

  async function saveBarcodeRegion(barcode) {
    if (!selectedField || !profile) { setMsg('Barcode: seleziona prima il campo.'); return }
    const box = barcode?.bbox || barcode
    if (!box || ![box.x0, box.y0, box.x1, box.y1].every((x) => Number.isFinite(Number(x)))) {
      setMsg('Barcode: coordinate non valide.')
      return
    }
    const valueText = cleanText(barcode?.text)
    const symbology = cleanText(barcode?.symbology)
    setBusy(true)
    try {
      await api.addRegion({
        profile_id: profile.profile_id, classe, docnumber: Number(docnumber), page_no: Number(barcode?.page_no || 1),
        x0: Number(box.x0), y0: Number(box.y0), x1: Number(box.x1), y1: Number(box.y1), created_by: author,
        field_name: selectedField, region_kind: 'barcode', unit: 'point',
        page_width: page1?.width, page_height: page1?.height,
        source: 'barcode', token_text: valueText ? valueText.slice(0, 400) : null,
        is_value_marked: !!valueText, barcode_symbology: symbology || null,
      })
      if (valueText) {
        await api.humanTruth({
          classe, docnumber: Number(docnumber), revision_id: 0, field_scope: 'testata',
          field_name: selectedField, h_value: valueText, h_author: author,
          h_reason: 'onboarding: marcatura barcode',
        })
      }
      setMarked((m) => [...m, {
        field_name: selectedField, profile_id: profile.profile_id,
        x0: Number(box.x0), y0: Number(box.y0), x1: Number(box.x1), y1: Number(box.y1),
        page_no: Number(barcode?.page_no || 1), region_kind: 'barcode',
        token_text: valueText || null, barcode_symbology: symbology || null,
      }])
      const e = await api.effective(classe); setEffective(e.rows || [])
      await refreshSelectedExtras()
      setSuggested(null)
      setMsg(`Barcode marcato per ${selectedField}${valueText ? ` = "${valueText}"` : ''}${symbology ? ` (${symbology})` : ''}`)
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }

  async function saveBarcodeRect(rect) {
    if (!selectedField || !profile) { setMsg('Barcode: seleziona prima il campo.'); return }
    setBusy(true)
    try {
      const saved = await api.addRegion({
        profile_id: profile.profile_id, classe, docnumber: Number(docnumber), page_no: 1,
        x0: rect.x0, y0: rect.y0, x1: rect.x1, y1: rect.y1, created_by: author,
        field_name: selectedField, region_kind: 'barcode', unit: 'point',
        page_width: page1?.width, page_height: page1?.height,
        source: 'rect', token_text: null, is_value_marked: false, barcode_symbology: null,
      })
      const decodedText = cleanText(saved?.text)
      const decodedSymbology = cleanText(saved?.symbology)
      setMarked((m) => [...m, { field_name: selectedField, profile_id: profile.profile_id,
        x0: rect.x0, y0: rect.y0, x1: rect.x1, y1: rect.y1, page_no: 1,
        region_kind: 'barcode', token_text: decodedText || null, barcode_symbology: decodedSymbology || null }])
      const e = await api.effective(classe); setEffective(e.rows || [])
      await refreshSelectedExtras()
      setSuggested(null)
      setMsg(decodedText
        ? `Barcode marcato per ${selectedField} = "${decodedText}"${decodedSymbology ? ` (${decodedSymbology})` : ''}`
        : `Area barcode marcata per ${selectedField}: nessun barcode decodificato in quest'area.`)
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }

  // clic sui campi multi-parola = inizio span; shift-clic sull'ultima parola = una sola regione.
  // sui campi token/singola parola il clic continua a salvare subito una regione da una parola.
  function markValue(word, shift) {
    if (!selectedField || !profile) return
    if (shift && lastWord && lastWord.field_name === selectedField) {
      const span = unionBox([lastWord.word, word])
      const ws = wordsInBox(span)
      const chosen = ws.length ? ws : [lastWord.word, word]
      const text = textOfWords(chosen)
      setLastWord(null)
      return saveRegion(unionBox(chosen), text, 'click')
    }
    if (selectedFieldIsMultiWord) {
      setLastWord({ field_name: selectedField, word })
      setMsg(`Inizio selezione ${selectedField}: ora shift-clic sull'ultima parola.`)
      return
    }
    setLastWord(null)
    return saveRegion({ x0: word.x0, y0: word.y0, x1: word.x1, y1: word.y1 }, word.text, 'click')
  }

  // rettangolo: dispatch per modalita' (valore | area-tabella | colonna | ancora multi-parola).
  function markRect(rect) {
    if (markMode === 'anchor') return markAnchorRect(rect)
    if (markMode === 'barcode') return saveBarcodeRect(rect)
    if (markMode === 'area') return saveAreaRegion(rect)
    if (markMode === 'gap_columns') { setMsg('Colonne a vuoti: usa "Rileva colonne dai vuoti", poi clicca una colonna numerata.'); return }
    if (markMode === 'table_area') return saveTableArea(rect)
    if (markMode === 'column') return saveColumn(rect)
    if (markMode === 'list') return saveListRegion(rect)
    if (markMode === 'footer') { setMsg('Piede: clicca le singole etichette per selezionarle/toglierle, poi conferma piede.'); return }
    if (PASSIVE_MODES.has(markMode)) { setMsg(`${activeMode.label}: nessuna marcatura sul documento in questo step.`); return }
    if (!selectedField || !profile) return
    const ws = wordsInBox(rect)
    if (ws.length) return saveRegion(unionBox(ws), textOfWords(ws), 'rect')
    return saveRegion(rect, '', 'rect')
  }

  async function saveAreaRegion(rect) {
    if (!profile) { setMsg('Carica prima un documento.'); return }
    if (!selectedField) { setMsg('Area geografica: seleziona prima un campo.'); return }
    setBusy(true)
    try {
      await api.addRegion({
        profile_id: profile.profile_id, classe, docnumber: Number(docnumber), page_no: 1,
        x0: rect.x0, y0: rect.y0, x1: rect.x1, y1: rect.y1, created_by: author,
        field_name: selectedField, region_kind: 'area', unit: 'point',
        page_width: page1?.width, page_height: page1?.height, source: 'rect',
        token_text: null, is_value_marked: false,
      })
      setMarked((m) => [...m, { field_name: selectedField, profile_id: profile.profile_id,
        x0: rect.x0, y0: rect.y0, x1: rect.x1, y1: rect.y1, region_kind: 'area', token_text: null }])
      const e = await api.effective(classe); setEffective(e.rows || [])
      await refreshSelectedExtras()
      setSuggested(null)
      setMsg(`Area geografica marcata per ${selectedField}: il valore sara' preso dai token interamente dentro il rettangolo.`)
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }

  // RIGHE (DEC-0046) - Area tabella: rettangolo attorno all'intera tabella. Struttura, non valore:
  // region_kind='table_area', field_name=NULL, nessun H.
  async function saveTableArea(rect) {
    if (!profile) { setMsg('Carica prima un documento.'); return }
    setBusy(true)
    try {
      await api.addRegion({
        profile_id: profile.profile_id, classe, docnumber: Number(docnumber), page_no: 1,
        x0: rect.x0, y0: rect.y0, x1: rect.x1, y1: rect.y1, created_by: author,
        field_name: null, region_kind: 'table_area', unit: 'point',
        page_width: page1?.width, page_height: page1?.height, source: 'rect',
      })
      setMarked((m) => [...m, { field_name: null, profile_id: profile.profile_id,
        x0: rect.x0, y0: rect.y0, x1: rect.x1, y1: rect.y1, region_kind: 'table_area', token_text: null }])
      setSuggested(null)
      setMsg('Area tabella marcata')
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }

  // RIGHE (DEC-0046) - Banda colonna: rettangolo la cui x0/x1 e' l'intervallo della colonna (y0/y1 = area).
  // Struttura, non valore: region_kind='column', field_name=campo colonna, nessun H.
  async function saveColumn(rect) {
    if (!profile) { setMsg('Carica prima un documento.'); return }
    if (!selectedField) { setMsg('Modalita\' Colonna: seleziona prima il campo-colonna a destra.'); return }
    setBusy(true)
    try {
      await api.addRegion({
        profile_id: profile.profile_id, classe, docnumber: Number(docnumber), page_no: 1,
        x0: rect.x0, y0: rect.y0, x1: rect.x1, y1: rect.y1, created_by: author,
        field_name: selectedField, region_kind: 'column', unit: 'point',
        page_width: page1?.width, page_height: page1?.height, source: 'rect',
      })
      setMarked((m) => [...m, { field_name: selectedField, profile_id: profile.profile_id,
        x0: rect.x0, y0: rect.y0, x1: rect.x1, y1: rect.y1, region_kind: 'column', token_text: null }])
      // DEC-0055: la riga in cima al box e' l'INTESTAZIONE della colonna -> la salviamo come ancora 'label'
      // (ponte UI->cmp_field_anchor per B-2: header-ancoraggio). E' un'ancora strutturale, non un H di valore.
      let headerText = ''
      const ws = wordsInBox(rect)
      if (ws.length) {
        const topY = Math.min(...ws.map((w) => w.y0))
        const topLine = ws.filter((w) => w.y0 <= topY + 6).sort((a, b) => a.x0 - b.x0)
        headerText = textOfWords(topLine)
      }
      if (headerText) {
        await api.addAnchor({
          profile_id: profile.profile_id, classe, field_name: selectedField,
          anchor_text: headerText, created_by: author, anchor_role: 'label', match_mode: 'normalized', weight: 1.0,
        })
        setAnchorsLocal((m) => ({ ...m, [selectedField]: headerText }))
      }
      const e = await api.effective(classe); setEffective(e.rows || [])
      setSuggested(null)
      setMsg(`Colonna marcata: ${selectedField}${headerText ? ` (header "${headerText}")` : ''}`)
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }

  async function saveListRegion(rect) {
    if (!profile) { setMsg('Carica prima un documento.'); return }
    if (!selectedField) { setMsg('Lista per forma: seleziona prima un campo-riga.'); return }
    if (!isRigaField(selectedField)) { setMsg("Lista per forma: il campo scelto non e' un campo-riga."); return }
    const form = cleanText(listFormDraft || selectedFieldForm)
    if (!form || form === 'testo_generico') { setMsg('Lista per forma: scegli una forma semantica specifica.'); return }
    setBusy(true)
    try {
      beginLongOp('list-extract', 'Estrazione lista...', 'Sto rileggendo l\'area ad alta risoluzione se serve. Attendi il completamento.')
      if (form !== selectedFieldForm) {
        await api.confirmForm({ classe, field_name: selectedField, semantic_form: form, author, reason: 'onboarding: lista per forma' })
        setFields((arr) => arr.map((x) => x.field_name === selectedField
          ? { ...x, semantic_form: form, form_source: 'confermata' } : x))
        setEffective((arr) => arr.map((x) => x.field_name === selectedField
          ? { ...x, semantic_form: form, form_source: 'confermata' } : x))
      }
      await api.addRegion({
        profile_id: profile.profile_id, classe, docnumber: Number(docnumber), page_no: 1,
        x0: rect.x0, y0: rect.y0, x1: rect.x1, y1: rect.y1, created_by: author,
        field_name: selectedField, region_kind: 'list', unit: 'point',
        page_width: page1?.width, page_height: page1?.height, source: 'rect',
      })
      const preview = await api.previewListExtract({
        classe,
        field_name: selectedField,
        docnumber: Number(docnumber),
        page: 1,
        box: { x0: rect.x0, y0: rect.y0, x1: rect.x1, y1: rect.y1 },
        dedup: true,
      })
      setListPreview(preview)
      setMarked((m) => [...m, { field_name: selectedField, profile_id: profile.profile_id,
        x0: rect.x0, y0: rect.y0, x1: rect.x1, y1: rect.y1, region_kind: 'list', token_text: null }])
      setSuggested(null)
      setMsg(`Lista per forma salvata: ${selectedField}, ${preview.n || 0} valori.`)
    } catch (e) { setMsg(userError(e)) } finally { finishLongOp(); setBusy(false) }
  }
  // modalita' ANCORA: clic su una parola/etichetta che governa il campo -> cmp_field_anchor (no H, no regione)
  async function markAnchor(word) {
    if (!selectedField || !profile) return
    const text = String(word.text || '').trim()
    if (!text) return
    setBusy(true)
    try {
      await api.addAnchor({
        profile_id: profile.profile_id, classe, field_name: selectedField,
        anchor_text: text, created_by: author, anchor_role: anchorRole === 'end' ? 'end' : 'label',
        match_mode: 'normalized', weight: 1.0,
      })
      if (anchorRole !== "end" && labelDirection) await saveLabelDirectionForField(selectedField, labelDirection)
      setMsg(`Ancora ${anchorRole === 'end' ? 'FINE' : 'inizio'} per ${selectedField}: "${text}"`)
      setAnchorsLocal((m) => ({ ...m, [selectedField]: text }))
      setSuggested(null)
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }

  // ANCORA VIRTUALE DI BORDO (DEC-0060): non un token, ma una RELAZIONE (bordo destro/sinistro), marcata con
  // Ctrl+Freccia. Il valore sara' letto tra l'etichetta e il bordo; intervallo vuoto -> valore vuoto (STOP).
  async function markEdgeAnchor(side, scope = 'page') {
    if (!selectedField || !profile) { setMsg('Seleziona prima un campo (modalita\' Ancora).'); return }
    const sentinels = {
      left: '$EDGE_LEFT$',
      right: '$EDGE_RIGHT$',
      top: scope === 'doc' ? '$EDGE_TOP_DOC$' : '$EDGE_TOP$',
      bottom: scope === 'doc' ? '$EDGE_BOTTOM_DOC$' : '$EDGE_BOTTOM$',
    }
    const sentinel = sentinels[side] || '$EDGE_RIGHT$'
    const sideLabel = {
      left: 'SINISTRO',
      right: 'DESTRO',
      top: scope === 'doc' ? 'ALTO DOCUMENTO' : 'ALTO PAGINA',
      bottom: scope === 'doc' ? 'BASSO DOCUMENTO' : 'BASSO PAGINA',
    }[side] || 'DESTRO'
    const scopeNote = (side === 'top' || side === 'bottom')
      ? ` (${scope === 'doc' ? 'documento' : 'pagina'})`
      : ''
    setBusy(true)
    try {
      await api.addAnchor({
        profile_id: profile.profile_id, classe, field_name: selectedField,
        anchor_text: sentinel, created_by: author, anchor_role: 'end', match_mode: 'normalized', weight: 1.0,
      })
      setMsg(`Ancora-bordo ${sideLabel} per ${selectedField}: valore tra l'etichetta e il bordo${scopeNote} (vuoto -> vuoto).`)
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }

  // Ctrl+Freccia -> marca l'ancora-bordo sul campo selezionato (solo in modalita' Ancora).
  useEffect(() => {
    function onKey(e) {
      if (!e.ctrlKey || markMode !== 'anchor' || !selectedField) return
      if (e.key === 'ArrowRight') { e.preventDefault(); markEdgeAnchor('right') }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); markEdgeAnchor('left') }
      else if (e.key === 'ArrowUp') { e.preventDefault(); markEdgeAnchor('top', e.shiftKey ? 'doc' : 'page') }
      else if (e.key === 'ArrowDown') { e.preventDefault(); markEdgeAnchor('bottom', e.shiftKey ? 'doc' : 'page') }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [markMode, selectedField, profile, classe, author, edgeScope])

  // RIGHE (DEC-0056) - Piede: l'operatore seleziona a toggle le singole etichette, non un rettangolo.
  // Cosi' esclude a mano i valori variabili (anche alfanumerici). Le etichette scelte -> ancora 'footer' del contenitore.
  async function saveFooterText(text) {
    if (!profile) { setMsg('Carica prima un documento.'); return }
    if (!tableContainer) { setMsg('Nessun contenitore-tabella (CLASSE...) in questa classe.'); return }
    if (!text || !text.trim()) { setMsg('Piede: seleziona una o piu\' etichette con clic singoli, poi conferma piede.'); return }
    setBusy(true)
    try {
      await api.addAnchor({
        profile_id: profile.profile_id, classe, field_name: tableContainer,
        anchor_text: text.trim(), created_by: author, anchor_role: 'footer', match_mode: 'normalized', weight: 1.0,
      })
      setMsg(`Piede marcato (${tableContainer}): "${text.trim()}"`)
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }
  // Azzera TUTTE le ancore-piede della tabella (sul contenitore, active+draft).
  async function clearFooters() {
    if (!classe || !tableContainer) { setMsg('Nessun contenitore-tabella per il piede.'); return }
    if (!window.confirm(`Azzerare tutte le ancore-piede della tabella (${tableContainer}) su questa classe?`)) return
    setBusy(true)
    try {
      const r = await api.resetFieldAnchors({ classe, field_name: tableContainer })
      setAnchorsLocal((m) => { const n = { ...m }; delete n[tableContainer]; return n })
      const e = await api.effective(classe); setEffective(e.rows || [])
      setMsg(`Piedi azzerati (${tableContainer}): ${r.deleted_anchors ?? 0} ancore rimosse`)
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }

  // Selezione del piede a TOGGLE: clic su OGNI etichetta per aggiungerla/toglierla (multi-clic).
  // Le etichette scelte si accumulano ed evidenziano; "conferma piede" le salva come ancora unica.
  function markFooterWord(word) {
    if (!profile) { setMsg('Carica prima un documento.'); return }
    if (!tableContainer) { setMsg('Nessun contenitore-tabella (CLASSE...) in questa classe.'); return }
    setFooterSel((sel) => {
      const has = sel.some((w) => w.i === word.i)
      const next = has ? sel.filter((w) => w.i !== word.i) : [...sel, word]
      setMsg(next.length
        ? `Piede: ${next.length} etichette selezionate. Ri-clicca per togliere, poi "conferma piede".`
        : 'Piede: nessuna etichetta selezionata.')
      return next
    })
  }

  // ordina le parole selezionate per lettura (riga poi colonna) e salva l'insieme come ancora unica
  function confirmFooterSel() {
    if (!footerSel.length) { setMsg('Piede: seleziona almeno un\'etichetta.'); return }
    const ord = [...footerSel].sort((a, b) => (a.y0 - b.y0) || (a.x0 - b.x0))
    const text = textOfWords(ord)
    setFooterSel([])
    return saveFooterText(text)
  }

  async function detectGapColumns() {
    if (!selectedField) { setMsg('Colonne a vuoti: seleziona prima un campo.'); return }
    if (!classe || !docnumber) { setMsg('Colonne a vuoti: scegli classe e docnumber.'); return }
    if (!page1?.words?.length) { setMsg('Colonne a vuoti: carica prima un campione con word-layer.'); return }
    setBusy(true)
    try {
      const preview = await api.previewFieldExtract(classe, selectedField, Number(docnumber))
      const cols = (preview.columns || []).map(previewColumnOverlay).filter(Boolean)
      const source = perimeterSourceLabel(preview.perimeter)
      if (!cols.length) {
        resetGapTool()
        setGapSource(source)
        setMsg(preview.message
          ? `Colonne a vuoti: ${preview.message}.`
          : 'Colonne a vuoti: nessuna colonna rilevata dal motore.')
        return
      }
      setGapColumns(cols)
      setGapExpected(Number(preview.expected_default || cols.length))
      setGapSelected(cols.length ? 1 : 0)
      setGapSource(source)
      setMsg(`Colonne a vuoti: rilevate ${cols.length} colonne (${source}). Clicca quella da usare.`)
    } catch (e) {
      resetGapTool()
      setMsg(String(e))
    } finally {
      setBusy(false)
    }
  }

  function selectGapColumn(index) {
    setGapSelected(Number(index))
    if (!gapExpected) setGapExpected(gapColumns.length)
    setMsg(`Colonne a vuoti: usero' la colonna ${index}.`)
  }

  async function refreshSelectedExtras() {
    if (!classe || !selectedField || !selectedExtraKey) return null
    const data = await api.fieldExtras(classe, selectedField)
    setFieldExtras((m) => ({ ...m, [selectedExtraKey]: data }))
    return data
  }


  async function saveLabelDirectionForField(fieldName, direction) {
    if (!classe || !fieldName) return
    const normalized = normalizeLabelDirection(direction)
    if (!normalized) return
    await api.setLabelDirection({
      classe,
      field_name: fieldName,
      label_direction: normalized,
      author,
      reason: "onboarding: direzione prossimita label",
    })
    const [f, e] = await Promise.all([api.fields(classe), api.effective(classe)])
    setFields(f.fields || [])
    setEffective(e.rows || [])
    const key = classe + "::" + fieldName
    if (fieldExtras[key]) {
      const data = await api.fieldExtras(classe, fieldName)
      setFieldExtras((m) => ({ ...m, [key]: data }))
    }
  }

  async function saveLabelDirection(direction) {
    if (!classe || !selectedField) return
    const normalized = normalizeLabelDirection(direction)
    setLabelDirection(normalized)
    setBusy(true)
    try {
      await saveLabelDirectionForField(selectedField, normalized)
      setMsg("Direzione label per " + selectedField + ": " + LABEL_DIRECTION_LABELS[normalized] + ".")
    } catch (e) {
      setMsg(userError(e))
    } finally {
      setBusy(false)
    }
  }

  async function saveInheritFromTestata(sourceField) {
    if (!classe || !selectedField) return
    const source = cleanText(sourceField)
    setBusy(true)
    try {
      if (source) {
        await api.setFieldInheritTestata({
          classe,
          field_name: selectedField,
          testata_field_name: source,
          author,
          reason: 'onboarding: campo-riga ereditato dalla testata',
        })
      } else {
        await api.clearFieldInheritTestata({
          classe,
          field_name: selectedField,
          author,
          reason: 'onboarding: rimuove eredita dalla testata',
        })
      }
      const [f, e] = await Promise.all([api.fields(classe), api.effective(classe)])
      setFields(f.fields || [])
      setEffective(e.rows || [])
      await refreshSelectedExtras()
      setMsg(source
        ? `Campo-riga ${selectedField}: eredita dalla testata ${source}.`
        : `Campo-riga ${selectedField}: eredita dalla testata rimossa.`)
    } catch (e) {
      setMsg(userError(e))
    } finally {
      setBusy(false)
    }
  }


  async function saveGapExtract() {
    if (!classe || !selectedField) return
    const col = Number(gapSelected)
    const expected = Number(gapExpected)
    if (!col || !expected) { setMsg('Colonne a vuoti: scegli colonna e colonne attese.'); return }
    if (col < 1 || expected < 1 || col > expected) {
      setMsg('Colonne a vuoti: la colonna deve essere tra 1 e le colonne attese.')
      return
    }
    setBusy(true)
    try {
      await api.setFieldExtract(classe, selectedField, { colonna_index: col, colonne_attese: expected })
      await refreshSelectedExtras()
      setMsg(`Colonne a vuoti salvato: colonna ${col} su ${expected}.`)
    } catch (e) {
      setMsg(String(e))
    } finally {
      setBusy(false)
    }
  }

  async function clearGapExtract() {
    if (!classe || !selectedField) return
    setBusy(true)
    try {
      const r = await api.clearFieldExtract(classe, selectedField)
      await refreshSelectedExtras()
      setMsg(r.cleared ? 'Colonne a vuoti rimosso.' : 'Colonne a vuoti gia assente.')
    } catch (e) {
      setMsg(String(e))
    } finally {
      setBusy(false)
    }
  }

  // modalita' ANCORA con rettangolo: ancora MULTI-PAROLA (etichette come "Vs Ordine", "Codice Postale")
  async function markAnchorRect(rect) {
    if (!selectedField || !profile) return
    const ws = wordsInBox(rect)
    const text = textOfWords(ws)
    if (!text) { setMsg("Nessuna parola nel rettangolo per l'ancora."); return }
    setBusy(true)
    try {
      await api.addAnchor({
        profile_id: profile.profile_id, classe, field_name: selectedField,
        anchor_text: text, created_by: author, anchor_role: anchorRole === 'end' ? 'end' : 'label',
        match_mode: 'normalized', weight: 1.0,
      })
      if (anchorRole !== "end" && labelDirection) await saveLabelDirectionForField(selectedField, labelDirection)
      setMsg(`Ancora ${anchorRole === 'end' ? 'FINE' : 'inizio'} (multi-parola) per ${selectedField}: "${text}"`)
      setAnchorsLocal((m) => ({ ...m, [selectedField]: text }))
      setSuggested(null)
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }

  function onWord(word, shift) {
    if (markMode === 'anchor') return markAnchor(word)
    if (markMode === 'barcode') { setMsg('Barcode: clicca il riquadro barcode rilevato o disegna un rettangolo attorno al codice.'); return }
    if (markMode === 'area') { setMsg('Area geografica: disegna un rettangolo; il clic sulla singola parola resta nello strumento Valore.'); return }
    if (markMode === 'footer') return markFooterWord(word)
    if (markMode === 'gap_columns') { setMsg('Colonne a vuoti: usa il rilevamento e clicca una colonna numerata.'); return }
    if (markMode === 'list') { setMsg("Lista per forma: disegna un rettangolo sull'area dei valori."); return }
    if (PASSIVE_MODES.has(markMode)) { setMsg(`${activeMode.label}: nessuna marcatura sul documento in questo step.`); return }
    if (markMode === 'table_area' || markMode === 'column') {
      setMsg("Modalita' righe: disegna un rettangolo (area o banda-colonna), non cliccare la parola.")
      return
    }
    return markValue(word, shift)
  }

  // fase B: conferma l'ancora suggerita -> cmp_field_anchor
  async function confirmSuggested() {
    if (!suggested || !profile) return
    setBusy(true)
    try {
      await api.addAnchor({
        profile_id: profile.profile_id, classe, field_name: suggested.field_name,
        anchor_text: suggested.text, created_by: author, anchor_role: 'label', match_mode: 'normalized', weight: 1.0,
      })
      setMsg(`Ancora confermata per ${suggested.field_name}: "${suggested.text}"`)
      setAnchorsLocal((m) => ({ ...m, [suggested.field_name]: suggested.text }))
      setSuggested(null)
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }

  // Reset di un campo sul documento corrente: rimuove le regioni e disattiva l'H (per ri-marcare pulito)
  async function resetField(f) {
    if (!profile || !docnumber) return
    setBusy(true)
    try {
      const r = await api.resetField({
        profile_id: profile.profile_id, classe, docnumber: Number(docnumber),
        field_scope: 'testata', field_name: f.field_name,
      })
      setMarked((m) => m.filter((x) => x.field_name !== f.field_name))
      setAnchorsLocal((m) => { const n = { ...m }; delete n[f.field_name]; return n })
      if (selectedField === f.field_name) setLastWord(null)
      const e = await api.effective(classe); setEffective(e.rows || [])
      setMsg(`Reset ${f.field_name}: ${r.deleted_regions ?? 0} regioni, ${r.deleted_anchors ?? 0} ancore (bozza)`)
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }

  // Cancella SOLO le ancore di un campo (active+draft), senza toccare regioni/H (metodo dedicato).
  async function resetFieldAnchors(f) {
    if (!classe) return
    const label = (f.arxivar_label && String(f.arxivar_label).trim()) || f.field_name
    if (!window.confirm(`Rimuovere l'ancora di "${label}" (su Board attiva e bozza)?`)) return
    setBusy(true)
    try {
      const r = await api.resetFieldAnchors({ classe, field_name: f.field_name })
      setAnchorsLocal((m) => { const n = { ...m }; delete n[f.field_name]; return n })
      const e = await api.effective(classe); setEffective(e.rows || [])
      setMsg(`Ancora rimossa da ${f.field_name}: ${r.deleted_anchors ?? 0}`)
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }

  // Reset di un campo su TUTTI i campioni della classe (profili active+draft): rimuove le regioni ovunque
  // e disattiva l'H. Tocca anche la Board attiva -> conferma esplicita. Per ripartire da zero sul campo.
  async function resetFieldAllSamples(f) {
    if (!classe) return
    const label = (f.arxivar_label && String(f.arxivar_label).trim()) || f.field_name
    if (!window.confirm(`Reset di "${label}" su TUTTI i campioni (anche sulla Board attiva).\n`
        + `Le marcature di questo campo verranno rimosse ovunque e l'H disattivato. Procedere?`)) return
    setBusy(true)
    try {
      const r = await api.resetFieldAllSamples({ classe, field_name: f.field_name, field_scope: 'testata' })
      setMarked((m) => m.filter((x) => x.field_name !== f.field_name))
      setAnchorsLocal((m) => { const n = { ...m }; delete n[f.field_name]; return n })
      if (selectedField === f.field_name) setLastWord(null)
      const e = await api.effective(classe); setEffective(e.rows || [])
      setMsg(`Reset ${f.field_name} su tutti i campioni: ${r.deleted_regions ?? 0} regioni / ${r.n_docs ?? 0} doc, ${r.deleted_anchors ?? 0} ancore, H off ${r.deactivated_h ?? 0}`)
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }

  // DEC-0050: conferma dei TIPI (forme). La forma e' un tipo-valore proposto (inferita) finche' non
  // e' confermata da un umano. Qui l'operatore conferma/corregge; scrive l'override autoritativo.
  // Splitter: trascinando verso SINISTRA il pannello destro si allarga. Larghezza persistita.
  function startResize(e) {
    e.preventDefault()
    if (dock === 'bottom') {                            // ancorato sotto: trascino verticale = altezza
      const startY = e.clientY
      const startH = panelH
      let lastH = startH
      function moveY(ev) { lastH = Math.min(700, Math.max(160, startH + (startY - ev.clientY))); setPanelH(lastH) }
      function upY() {
        document.removeEventListener('mousemove', moveY)
        document.removeEventListener('mouseup', upY)
        document.body.style.cursor = ''
        try { window.localStorage.setItem(PANEL_H_KEY, String(Math.round(lastH))) } catch { /* ignore */ }
      }
      document.body.style.cursor = 'row-resize'
      document.addEventListener('mousemove', moveY)
      document.addEventListener('mouseup', upY)
      return
    }
    const startX = e.clientX                            // ancorato a destra: trascino orizzontale = larghezza
    const startW = panelW
    let lastW = startW
    function move(ev) { lastW = Math.min(900, Math.max(280, startW + (startX - ev.clientX))); setPanelW(lastW) }
    function up() {
      document.removeEventListener('mousemove', move)
      document.removeEventListener('mouseup', up)
      document.body.style.cursor = ''
      try { window.localStorage.setItem(PANEL_W_KEY, String(Math.round(lastW))) } catch { /* ignore */ }
    }
    document.body.style.cursor = 'col-resize'
    document.addEventListener('mousemove', move)
    document.addEventListener('mouseup', up)
  }

  async function openForms() {
    if (!classe) { setMsg('Scegli prima una classe'); return }
    try {
      if (!formsCat.length || !formsAreaOrder.length) {
        const c = await api.formsCatalog()
        setFormsCat(c.forms || [])
        setFormsAreaOrder(c.area_order || [])
        setFormsAreaLabels(c.area_labels || {})
      }
      setFormEdits({}); setShowForms(true)
    } catch (e) { setMsg(String(e)) }
  }
  async function confirmFieldForm(f, form) {
    setBusy(true)
    try {
      const sf = (form && form !== 'testo_generico') ? form : null
      await api.confirmForm({ classe, field_name: f.field_name, semantic_form: sf, author, reason: 'onboarding: conferma tipo' })
      setFields((arr) => arr.map((x) => x.field_name === f.field_name
        ? { ...x, semantic_form: sf, form_source: 'confermata' } : x))
      setMsg(`Tipo confermato per ${f.field_name}: ${form}`)
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }

  async function saveDateConvention(conv) {
    if (!classe || !selectedField) return
    setBusy(true)
    try {
      await api.setDateConvention({
        classe,
        field_name: selectedField,
        date_convention: conv,
        author,
        reason: 'onboarding: convenzione data per campo',
      })
      const [f, e] = await Promise.all([api.fields(classe), api.effective(classe)])
      setFields(f.fields || [])
      setEffective(e.rows || [])
      setFieldExtras((m) => { const next = { ...m }; delete next[selectedExtraKey]; return next })
      setMsg(`Convenzione data salvata per ${selectedField}: ${DATE_CONVENTION_LABELS[conv] || conv}`)
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }

  async function activate() {
    if (!profile) return
    if (!classCanProceed) {
      setMsg('Attivazione sospesa: dichiara prima la classe per lo scenario di scrittura.')
      return
    }
    if (foreignRegionCount > 0) {
      setMsg(`${foreignRegionCount} regioni di un'altra versione su questo documento. Premi "Consolida bozza" per rimuovere i residui, poi Attiva.`)
      return
    }
    setBusy(true)
    try {
      await api.activate(profile.profile_id, author)
      const e = await api.effective(classe); setEffective(e.rows || [])
      setMsg(`Board v${profile.version} attivata`)
    } catch (e) { setMsg(String(e)) } finally { setBusy(false) }
  }

  const arithAmountRoles = arithData?.amount_roles || []
  const arithRowRoles = arithData?.row_roles || []
  const arithRules = (arithData?.rules || []).filter((r) => r.enabled !== false)
  const arithPreview = previewArithDraft(arithDraft)

  return (
    <div className={`app tone-${activeMode.tone}`} aria-busy={!!longOp}>
      <header>
        <b>TT-ARX-AI {'\u2014'} Onboarding</b>
        <button className="theme-toggle" onClick={() => chooseTheme(theme === 'dark' ? 'light' : 'dark')}
                title="Cambia tema chiaro/scuro">{theme === 'dark' ? 'Tema chiaro' : 'Tema scuro'}</button>
      </header>

      <div className="toolbar">
        <label>Chiave API
          <input type="password" value={apiKeyDraft} onChange={(e) => setApiKeyDraft(e.target.value)} />
        </label>
        <button disabled={busy || !apiKeyDraft} onClick={connect} title="Connetti al backend con la chiave API e carica l'elenco classi">Connetti</button>
        {(() => {
          const onboarded = classes.filter((c) => isProfileActive(c))
          const toOnboard = classes.filter((c) => !isProfileActive(c) && c.is_leaf)
          const azureOnb = onboarded.filter((c) => c.has_azure)
          const nonAzureOnb = onboarded.filter((c) => !c.has_azure)
          const inOnboarded = classe && onboarded.some((c) => c.classe === classe)
          const inToOnboard = classe && toOnboard.some((c) => c.classe === classe)
          const opt = (c) => <option key={c.classe} value={c.classe}>{c.classe} (v{c.profilo_attivo}, {c.n_campi})</option>
          return (<>
            <label>Onboardate
              <select value={inOnboarded ? classe : ''} onChange={(e) => e.target.value && selectClass(e.target.value)} disabled={busy || !onboarded.length}>
                <option value="">-- {onboarded.length} onboardate --</option>
                {azureOnb.length ? <optgroup label="Azure">{azureOnb.map(opt)}</optgroup> : null}
                {nonAzureOnb.length ? <optgroup label="Non-Azure">{nonAzureOnb.map(opt)}</optgroup> : null}
              </select>
            </label>
            <label>Da onboardare
              <select value={inToOnboard ? classe : ''} onChange={(e) => e.target.value && selectClass(e.target.value)} disabled={busy || !toOnboard.length}>
                <option value="">-- {toOnboard.length} disponibili --</option>
                {toOnboard.map((c) => <option key={c.classe} value={c.classe}>{c.classe} ({c.n_campi})</option>)}
              </select>
            </label>
          </>)
        })()}
        {classe ? <span className={`external-chip ${externalGateChip.tone}`}>{externalGateChip.text}</span> : null}
        {needsDeclaration ? (
          <button title="Genera il catalogo campi, adotta la classe per l'onboarding deterministico e verifica gli external_id reali." disabled={busy || !classe} onClick={declareClass}>Dichiara classe</button>
        ) : null}
        <label>Docnumber
          <input value={docnumber} disabled={busy} onChange={(e) => setDocnumber(e.target.value.replace(/\D/g, ''))} placeholder="es. 24312" />
        </label>
        <label>Operatore
          <input value={author} disabled={busy} onChange={(e) => setAuthor(e.target.value)} />
        </label>
        <button disabled={busy || !classe || !docnumber || !classCanProceed} onClick={() => loadSample()} title="Scarica il documento e costruisci l'anteprima con lo strato-parole">Carica documento</button>
        <button disabled={busy || !classe || !docnumber || !classCanProceed}
                onClick={() => loadSample(undefined, { forceOcr: true })}
                title="Riesegue l'OCR ignorando la cache; se un OCR e' appena stato completato, riusa quel risultato.">Rifai OCR (forza)</button>
        <button disabled={busy || !docnumber || !page1}
                onClick={() => refreshBarcode(true)}
                title="Riesegue la decodifica barcode del documento caricato">Rileva barcode</button>
        <button disabled={busy || !classe || !docnumber || !page1 || !classCanProceed || classIsAzure}
                onClick={applyProfileToDocument}
                title="Applica la Board attiva al documento caricato e salva le marcature localizzate nella bozza">Riporta le regole su questo documento</button>
        {foreignRegionCount > 0 ? (
          <button disabled={busy || !classe || !docnumber || !profile || !classCanProceed}
                  onClick={consolidateDraft}
                  title="Rimuove le regioni auto residue di un'altra versione su questo documento, cosi' puoi attivare la bozza">Consolida bozza</button>
        ) : null}
        <button disabled={busy || !profile || !classCanProceed} onClick={activate} title="Attiva la versione della Board per la classe (rende effettive le marcature)">Attiva Board</button>
        <button onClick={() => setShowDone((s) => !s)} title="Mostra/nasconde le coppie campo-valore gia' marcate">
          {showDone ? `Nascondi fatti (${doneCount})` : `Mostra fatti (${doneCount})`}
        </button>
        <button className={showAllMarks ? 'on' : ''} onClick={() => setShowAllMarks((s) => !s)} title="Disegna TUTTE le marcature della Board sul documento (aree valore, colonne e area tabella) senza dover selezionare un campo">
          {showAllMarks ? 'Marcature: tutte' : 'Marcature: contestuali'}
        </button>
      </div>

      <div className="worklist">
        <span className="wl-title">Campioni {classe ? <span className="muted">{classe}</span> : <span className="muted">(default)</span>} ({worklist.length})</span>
        <label className="wl-file" title="Carica i campioni per classe da un file .json">
          Carica .json
          <input type="file" accept=".json,application/json" style={{ display: 'none' }}
                 onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) importWorklistsFile(f); e.target.value = '' }} />
        </label>
        {worklist.map((it, i) => (
          <button key={it.doc}
                  className={'wl-chip' + ((i === wlIdx || String(docnumber) === String(it.doc)) ? ' cur' : '')}
                  title={it.hint || it.doc}
                  disabled={busy || !classe}
                  onClick={() => loadFromWorklist(i)}>
            {it.doc}
            <span className="wl-x" title="rimuovi"
                  onClick={(e) => { e.stopPropagation(); removeFromWorklist(i) }}>{'\u00d7'}</span>
          </button>
        ))}
        <input className="wl-add" value={wlAdd}
               onChange={(e) => setWlAdd(e.target.value.replace(/\D/g, ''))} placeholder="+ doc" />
        <button disabled={!wlAdd} onClick={addToWorklist} title="Aggiungi il docnumber digitato alla lista campioni della classe">Aggiungi</button>
        <button disabled={busy || !classe || !worklist.length}
                title="Carica il prossimo campione della lista (torna al primo dopo l'ultimo)"
                onClick={() => loadFromWorklist(wlIdx + 1 < worklist.length ? wlIdx + 1 : 0)}>Prossimo</button>
      </div>

      {wlIdx >= 0 && worklist[wlIdx]?.hint && <div className="wl-hint">{worklist[wlIdx].hint}</div>}

      {longOp && (
        <div className="long-op" role="status" aria-live="polite">
          <span className="long-op-spinner" aria-hidden="true" />
          <div className="long-op-copy">
            <b>{longOp.label}</b>
            <span>{longOpElapsed}s</span>
            {longOpSlow ? <em>{longOp.slowText || 'Operazione lunga, ancora in corso.'}</em> : null}
          </div>
        </div>
      )}

      {msg && <div className="msg">{msg}</div>}

      {externalCheck && !externalCheck.loading && (!externalCheck.ok || externalCheck.child_schema_missing) && (
        <div className="external-gate" aria-live="polite">
          <div className="external-gate-head">
            <b>External_id mancanti</b>
            <button disabled={busy || !classe} onClick={recheckExternalIds} title="Rilegge la struttura ARXivar, rigenera catalogo e schema figlia, poi verifica senza scrivere external_id">Ri-sincronizza e verifica</button>
          </div>
          <p>
            I campi vanno valorizzati in ARXivar dall'amministratore; TTAI rilegge e verifica senza scrivere external_id.
          </p>
          {externalCheck.child_schema_missing ? <p className="external-gate-warn">Schema ClasseBox figlia non presente per questa classe.</p> : null}
          {externalCheck.missing?.length ? (
            <ul>
              {externalCheck.missing.map((item, idx) => (
                <li key={`${item.scope}-${item.field_name}-${idx}`}>
                  <span>{item.scope}</span>
                  <code>{missingExternalLabel(item)}</code>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}

      {suggested && (
        <div className="suggest">
          <span>Ancora suggerita per <b>{suggested.field_name}</b>: "{suggested.text}"</span>
          <button disabled={busy} onClick={confirmSuggested} title="Salva l'ancora suggerita come etichetta del campo">Conferma ancora</button>
          <button onClick={() => setSuggested(null)} title="Scarta il suggerimento di ancora">Ignora</button>
          <span className="hint2">(o passa a modalita' Ancora e clicca quella giusta)</span>
        </div>
      )}

      <div className="v2-modebar">
        <div className="family-tabs" role="tablist" aria-label="Famiglie onboarding">
          {FAMILY_TABS.map((tab) => (
            <span key={tab.key} className="family-tab-wrap">
              <button type="button"
                      className={activeFamily === tab.key ? 'on' : ''}
                      disabled={tab.key === 'table' && !hasRowFields}
                      title={tab.key === 'table' && !hasRowFields ? `${tab.label} (nessun campo riga)` : tab.label}
                      onClick={() => selectFamily(tab.key)}>
                <span className="family-icon">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
              <button type="button"
                      className="family-help"
                      title={`Come funziona: ${tab.label}`}
                      aria-label={`Come funziona: ${tab.label}`}
                      onClick={(e) => { e.stopPropagation(); openExplainer(tab.helpHash) }}>
                ?
              </button>
            </span>
          ))}
        </div>
        <div className={`mode-chip tone-${activeMode.tone}`}>
          <span className="chip-icon">{activeMode.icon}</span>
          <span>{activeMode.label}</span>
          {selectedFieldInfo ? (
            <span className="chip-field">
              {(selectedFieldInfo.arxivar_label && String(selectedFieldInfo.arxivar_label).trim()) || selectedFieldInfo.field_name}
            </span>
          ) : null}
        </div>
      </div>

      <div className={`main dock-${dock}`}>
        <div className="canvas-col">
          {page1
            ? <PageCanvas pageUrl={pageUrl} words={page1.words || []}
                          pageWidth={page1.width} pageHeight={page1.height} scale={scale}
                          marked={marked} maskDone={!showDone} selectedField={selectedField}
                          wordsActive={canvasWordsActive}
                          highlight={markMode === 'footer' ? footerSel : null}
                          intent={intentHighlight}
                          barcodes={page1Barcodes}
                          barcodesActive={markMode === 'barcode' && !!selectedField}
                          columnOverlays={gapColumnOverlays}
                          mode={markMode}
                          onColumnClick={selectGapColumn}
                          onBarcodeClick={saveBarcodeRegion}
                          onWordClick={onWord} onRectDraw={markRect} />
            : <div className="placeholder">Scegli classe + docnumber e premi "Carica documento".</div>}
        </div>

        <div className={`resizer resizer-${dock}`} onMouseDown={startResize}
             title={dock === 'bottom' ? 'Trascina su/giu per l\'altezza del pannello' : 'Trascina per allargare/stringere il pannello'} />

        <aside className="panel" style={dock === 'bottom' ? { height: panelH, flex: '0 0 auto' } : { width: panelW, flex: '0 0 auto' }}>
          <div className="dock-toggle">
            <span className="muted">Pannello</span>
            <button type="button" className={dock === 'right' ? 'on' : ''} onClick={() => chooseDock('right')} title="Ancora a destra">Destra</button>
            <button type="button" className={dock === 'bottom' ? 'on' : ''} onClick={() => chooseDock('bottom')} title="Ancora sotto">Sotto</button>
          </div>
          <h3>Campi {classe && <span className="muted">- {classe}</span>}
            {classe ? <button className="btn-forms" onClick={openForms} title="Conferma i tipi (forme) proposti">Conferma tipi</button> : null}
          </h3>
          {hasRowFields && (
            <label className="only-testata">
              <input type="checkbox" checked={onlyTestata} onChange={(e) => setOnlyTestata(e.target.checked)} />
              solo testata (nasconde {rowFieldCount} campi riga)
            </label>
          )}
          <div className="zones">
          <details className="zone" open style={{ '--zg': zoneGrow[0], order: 3 }}><summary>Strumento attivo</summary>
          <div className="markmode">
            <span className="mm-label">Marca:</span>
            <button className={markMode === 'value' ? 'on' : ''} title="Marca il valore del campo: clicca una parola o disegna un rettangolo"
                    onClick={() => switchMode('value')}>Valore</button>
            <button className={markMode === 'area' ? 'on' : ''} title="Area geografica: prende tutti i token interamente dentro il rettangolo, senza ancore"
                    onClick={() => switchMode('area')}>Area</button>
            <button className={markMode === 'barcode' ? 'on' : ''} title="Marca un barcode/QR/DataMatrix rilevato come sorgente del campo"
                    onClick={() => switchMode('barcode')}>Barcode</button>
            <button className={markMode === 'anchor' ? 'on' : ''} title="Marca l'etichetta/ancora e il perimetro del campo"
                    onClick={() => switchMode('anchor')}>Ancora</button>
            <button className={markMode === 'gap_columns' ? 'on' : ''} title="Segmenta la riga in colonne dai vuoti e scegli quale usare"
                    onClick={() => switchMode('gap_columns')}>Colonne a vuoti</button>
            {hasRowFields && (<>
              <span className="mm-sep">righe:</span>
              <button className={markMode === 'table_area' ? 'on' : ''} title="Delimita l'area della tabella delle righe"
                      onClick={() => switchMode('table_area')}>Area tabella</button>
              <button className={markMode === 'column' ? 'on' : ''} title="Marca una colonna della tabella (dall'intestazione)"
                      onClick={() => switchMode('column')}>Colonna</button>
              <button className={markMode === 'list' ? 'on' : ''} title="Marca un'area e genera una riga per ogni valore compatibile con la forma del campo"
                      onClick={() => switchMode('list')}>Lista per forma</button>
              <button className={markMode === 'footer' ? 'on' : ''} title="Marca il piede tabella (etichette dei totali)"
                      onClick={() => switchMode('footer')}>Piede</button>
              {markMode === 'footer' && (<>
                <button className="btn-forms" disabled={busy || !footerSel.length}
                        title="Salva le etichette selezionate come piede" onClick={confirmFooterSel}>conferma piede ({footerSel.length})</button>
                <button disabled={busy || !footerSel.length}
                        title="Svuota la selezione corrente" onClick={() => { setFooterSel([]); setMsg('Piede: selezione svuotata.') }}>annulla</button>
              </>)}
              <button className="fld-reset-all" disabled={busy || !tableContainer}
                      title="Rimuove tutte le ancore-piede della tabella" onClick={clearFooters}>azzera piedi</button>
            </>)}
          </div>
          {markMode === 'anchor' && (
            <div className="markmode anchorrole">
              <span className="mm-label">Ancora:</span>
              <button className={anchorRole === 'start' ? 'on' : ''}
                      onClick={() => setAnchorRole('start')} title="Etichetta d'inizio (normale)">inizio</button>
              <button className={anchorRole === 'end' ? 'on' : ''}
                      onClick={() => setAnchorRole('end')} title="Etichetta di fine: il valore sta TRA inizio e fine (solo casi particolari)">fine</button>
              <span className="mm-sep">direzione:</span>
              {LABEL_DIRECTIONS.map((d) => (
                <button key={d.value} disabled={busy || !selectedField || anchorRole === "end"}
                        className={labelDirection === d.value ? "on" : ""}
                        title={d.title}
                        onClick={() => saveLabelDirection(d.value)}>{d.label}</button>
              ))}
              <span className="mm-sep">bordo:</span>
              <button disabled={busy || !selectedField} title="Ancora-bordo SINISTRO (Ctrl+Freccia sx): valore tra bordo e etichetta"
                      onClick={() => markEdgeAnchor('left')}>{'\u25c0'} bordo</button>
              <button disabled={busy || !selectedField} title="Ancora-bordo DESTRO (Ctrl+Freccia dx): valore tra etichetta e bordo; se vuoto -> vuoto"
                      onClick={() => markEdgeAnchor('right')}>bordo {'\u25b6'}</button>
              <span className="mm-sep">verticale:</span>
              <button className={edgeScope === 'page' ? 'on' : ''}
                      onClick={() => setEdgeScope('page')} title="Bordo verticale sulla pagina corrente">pagina</button>
              <button className={edgeScope === 'doc' ? 'on' : ''}
                      onClick={() => setEdgeScope('doc')} title="Bordo verticale sull'intero documento">documento</button>
              <button disabled={busy || !selectedField} title="Ancora-bordo ALTO (Ctrl+Freccia su; Ctrl+Shift+Freccia su = documento)"
                      onClick={() => markEdgeAnchor('top', edgeScope)}>{'\u25b2'} bordo</button>
              <button disabled={busy || !selectedField} title="Ancora-bordo BASSO (Ctrl+Freccia giu; Ctrl+Shift+Freccia giu = documento)"
                      onClick={() => markEdgeAnchor('bottom', edgeScope)}>bordo {'\u25bc'}</button>
            </div>
          )}
          <p className="hint">{
            markMode === 'anchor'
              ? <>Modalita ancore: la direzione sotto/sopra usa il valore vicino nella direzione scelta, anche se spostato a sinistra o destra. I bordi restano un perimetro separato. Modalita' <b>Ancora</b> ({anchorRole === 'end' ? <b>fine</b> : <b>inizio</b>}): clic su una parola-etichetta o <b>rettangolo</b> per una multi-parola. L'ancora <b>fine</b> serve nei casi in cui B deve prendere il valore <b>tra</b> due etichette, anche sopra/sotto se sono nella stessa colonna. <b>Ancora-bordo</b> (<b>Ctrl+Freccia</b> dx/sx/su/giu, <b>Ctrl+Shift</b> su/giu per documento, o bottoni "bordo"): il valore sta tra l'etichetta e il bordo pagina/documento; se l'area e' <b>vuota</b> il valore e' <b>vuoto</b>.</>
            : markMode === 'gap_columns'
              ? <>Strumento <b>Colonne a vuoti</b>: chiede al motore l'anteprima dentro il perimetro del campo e salva l'opt-in con guardia sul numero di colonne.</>
            : markMode === 'barcode'
              ? <>Modalita' <b>Barcode</b>: seleziona un campo, poi clicca il rettangolo barcode rilevato oppure disegna un rettangolo attorno al codice. Il valore decodificato resta sorgente barcode e viene validato dalla forma del campo.</>
            : markMode === 'area'
              ? <>Modalita' <b>Area geografica</b>: seleziona un campo, poi disegna un <b>rettangolo</b>. Tutto cio' che e' <b>dentro</b> il rettangolo e coerente con la forma diventa il valore; testo generico = tutte le parole. E' assoluta, senza ancora, per documenti a layout fisso.</>
            : markMode === 'table_area'
              ? <>Modalita' <b>Area tabella</b>: disegna un <b>rettangolo</b> attorno all'intera tabella di dettaglio (dall'intestazione colonne fino all'ultima riga, prima dei totali). Delimita la banda-y utile; non serve selezionare un campo.</>
            : markMode === 'column'
              ? <>Modalita' <b>Colonna</b>: seleziona un <b>campo-colonna</b> (i campi CLASSE...), poi disegna un <b>rettangolo</b> partendo <b>dall'intestazione</b> della colonna. Conta l'intervallo <b>orizzontale</b> (x); la <b>riga in cima</b> al box e' l'header e viene salvata come <b>ancora</b> (cosi' B ritrova la colonna su ogni documento).</>
            : markMode === 'list'
              ? <>Modalita' <b>Lista per forma</b>: seleziona un <b>campo-riga</b>, scegli la forma, poi disegna un <b>rettangolo</b>. B creera' una riga per ogni valore trovato nell'area.</>
            : markMode === 'footer'
              ? <>Modalita' <b>Piede</b>: <b>clic</b> su ogni etichetta dei totali per selezionarla; <b>ri-clic</b> per toglierla. Le etichette selezionate si evidenziano in arancio. Poi usa <b>conferma piede</b>. Seleziona <b>solo le etichette</b>, escludendo valori variabili.</>
            : markMode === 'forms'
              ? <>Famiglia <b>Forme</b>: controlla e conferma il tipo di valore del campo selezionato.</>
            : markMode === 'rules'
              ? <>Famiglia <b>Regole</b>: dichiara controlli aritmetici per ruoli della classe.</>
            : markMode === 'summary'
              ? <>Famiglia <b>Riepilogo</b>: vista compatta della Board salvata per il campo selezionato.</>
            : <>Modalita' <b>Valore</b>: seleziona un campo, poi <b>clic</b> = una parola; <b>shift-clic</b> sull'ultima = da prima a ultima (una regione); o <b>rettangolo</b> attorno al valore.</>
          }</p>
          {markMode === 'gap_columns' && (
            <section className="gap-tool" aria-live="polite">
              <div className="gap-tool-head">
                <b>Colonne a vuoti</b>
                {selectedExtra?.extract?.mode === 'colonne_a_vuoti'
                  ? <span className="field-card-badge">attivo</span>
                  : <span className="field-card-badge">non attivo</span>}
              </div>
              <div className="gap-tool-actions">
                <button type="button" disabled={busy || !selectedField || !page1}
                        title="Chiede al motore l'anteprima delle colonne rilevate dai vuoti nel perimetro del campo"
                        onClick={detectGapColumns}>Rileva colonne dai vuoti</button>
                <button type="button" disabled={busy || !selectedField}
                        title="Rimuove la modalita' colonne-a-vuoti dal campo selezionato"
                        onClick={clearGapExtract}>Rimuovi modalita'</button>
              </div>
              {gapColumns.length ? (
                <>
                  <div className="gap-cols">
                    {gapColumns.map((col) => (
                      <button type="button" key={col.index}
                              className={Number(gapSelected) === Number(col.index) ? 'on' : ''}
                              title={col.text}
                              onClick={() => selectGapColumn(col.index)}>
                        {col.index}
                      </button>
                    ))}
                  </div>
                  <div className="gap-form">
                    <label>Usa la colonna
                      <input type="number" min="1" max={Math.max(1, gapExpected || gapColumns.length)}
                             value={gapSelected || ''}
                             onChange={(e) => setGapSelected(Number(e.target.value || 0))} />
                    </label>
                    <label>Colonne attese
                      <input type="number" min="1"
                             value={gapExpected || ''}
                             onChange={(e) => setGapExpected(Number(e.target.value || 0))} />
                    </label>
                  </div>
                  <p className="gap-note">Se le colonne rilevate non sono {gapExpected || gapColumns.length}: descrizione vuota -&gt; campo vuoto; testo anomalo -&gt; interviene l'LLM locale (Ollama) sul ritaglio; altrimenti va a giudizio.</p>
                  <button type="button" className="gap-save"
                          disabled={busy || !selectedField || !gapSelected || !gapExpected || Number(gapSelected) > Number(gapExpected)}
                          title="Salva la scelta colonne-a-vuoti (colonna da usare e colonne attese) per il campo"
                          onClick={saveGapExtract}>Salva</button>
                  {gapSource ? <p className="gap-source">Riga: {gapSource}</p> : null}
                </>
              ) : (
                <p className="gap-empty">Nessuna colonna rilevata.</p>
              )}
            </section>
          )}
          {markMode === 'list' && (
            <section className="gap-tool" aria-live="polite">
              <div className="gap-tool-head">
                <b>Lista per forma</b>
                {selectedField && isRigaField(selectedField)
                  ? <span className="field-card-badge">campo-riga</span>
                  : <span className="field-card-badge">scegli campo-riga</span>}
              </div>
              <div className="gap-form">
                <label>Forma
                  <select value={listFormDraft || selectedFieldForm || 'testo_generico'}
                          disabled={busy || !selectedField}
                          onChange={(e) => { setListFormDraft(e.target.value); setListPreview(null) }}>
                    {formGroups.length ? formGroups.map((group) => (
                      <optgroup key={group.area} label={group.label}>
                        {group.forms.map((f) => <option key={f.form} value={f.form}>{f.form}</option>)}
                      </optgroup>
                    )) : <option value={selectedFieldForm || 'testo_generico'}>{selectedFieldForm || 'testo_generico'}</option>}
                  </select>
                </label>
              </div>
              {listPreview ? (
                <>
                  <p className="gap-note">{listPreview.n || 0} valori in ordine di lettura.</p>
                  {(listPreview.values || []).length ? (
                    <ol className="rules-list">
                      {(listPreview.values || []).slice(0, 25).map((value, idx) => (
                        <li key={`${idx}-${value}`}><span>{value}</span></li>
                      ))}
                    </ol>
                  ) : <p className="gap-empty">Nessun valore compatibile trovato nell'area.</p>}
                </>
              ) : (
                <p className="gap-empty">Disegna il rettangolo sull'area dei valori.</p>
              )}
            </section>
          )}
          {markMode === 'rules' && (
            <section className="rules-panel" aria-live="polite">
              <div className="rules-head">
                <b>Regole</b>
                {arithLoading ? <span>aggiorno</span> : null}
              </div>
              {!arithAmountRoles.length ? (
                <p className="rules-empty">Nessun ruolo importo disponibile per questa classe.</p>
              ) : (
                <>
                  <div className="rules-toggle" role="tablist" aria-label="Tipo regola">
                    <button type="button" className={arithDraft.op === 'signed_sum' ? 'on' : ''}
                            title="Regola come somma di ruoli con segno (+/-)"
                            onClick={() => setRuleOp('signed_sum')}>somma con segno</button>
                    <button type="button" className={arithDraft.op === 'sumrows' ? 'on' : ''}
                            disabled={!arithRowRoles.length}
                            title="Regola come somma dei valori di riga di un ruolo"
                            onClick={() => setRuleOp('sumrows')}>somma righe</button>
                  </div>
                  <label className="rules-label">Valore
                    <select value={arithDraft.target_role} onChange={(e) => setRuleTarget(e.target.value)}>
                      {arithAmountRoles.map((r) => <option key={r.role} value={r.role}>{r.label}</option>)}
                    </select>
                  </label>
                  {arithDraft.op === 'signed_sum' ? (
                    <div className="rules-terms">
                      {(arithDraft.terms || []).map((term, idx) => (
                        <div className="rule-term" key={`${idx}-${term.role}`}>
                          <button type="button" className="term-sign"
                                  title="Inverti segno"
                                  onClick={() => updateTerm(idx, { sign: term.sign === '-' ? '+' : '-' })}>
                            {term.sign === '-' ? '-' : '+'}
                          </button>
                          <select value={term.role} onChange={(e) => updateTerm(idx, { role: e.target.value })}>
                            {arithAmountRoles.map((r) => <option key={r.role} value={r.role}>{r.label}</option>)}
                          </select>
                          <button type="button" className="term-remove" title="Rimuovi termine"
                                  disabled={(arithDraft.terms || []).length <= 1}
                                  onClick={() => removeRuleTerm(idx)}>x</button>
                        </div>
                      ))}
                      <button type="button" className="rules-secondary" title="Aggiungi un termine alla somma con segno" onClick={addRuleTerm}>Aggiungi termine</button>
                    </div>
                  ) : (
                    <label className="rules-label">Righe
                      <select value={arithDraft.rows_role} onChange={(e) => { setArithError(''); setArithConflict(null); setArithDraft((d) => ({ ...d, rows_role: e.target.value })) }}>
                        {arithRowRoles.map((r) => <option key={r.role} value={r.role}>{r.label}</option>)}
                      </select>
                    </label>
                  )}
                  <div className="rules-preview">{arithPreview || 'Anteprima non disponibile'}</div>
                  {arithError ? (
                    <div className="rules-alert">
                      <span>{arithError}</span>
                      {arithConflict ? (
                        <button type="button" disabled={busy || arithLoading} onClick={replaceArithRule} title="Sostituisci la regola esistente sullo stesso valore con quella corrente">
                          Sostituisci
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  <button type="button" className="rules-primary"
                          disabled={busy || arithLoading || !arithPreview}
                          title="Salva la regola aritmetica dichiarata per la classe"
                          onClick={saveArithRule}>Dichiara regola</button>
                </>
              )}
              <div className="rules-list-head">
                <b>Dichiarate</b>
                <button type="button" className="rules-refresh" disabled={arithLoading || !classe}
                        title="Aggiorna" onClick={() => loadArithRules({ silent: true })}>Aggiorna</button>
              </div>
              {arithRules.length ? (
                <ul className="rules-list">
                  {arithRules.map((rule) => (
                    <li key={rule.rule_id}>
                      <span>{formatRule(rule)}</span>
                      <button type="button" title="Rimuovi regola"
                              disabled={busy}
                              onClick={() => disableArithRule(rule)}>x</button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="rules-empty">Nessuna regola dichiarata.</p>
              )}
            </section>
          )}
          </details>
          <div className="zsplit" onMouseDown={(e) => startZoneResize(e, 0, 1)} style={{ order: 4 }} title="Trascina per ridimensionare le zone" />
          <details className="zone" open style={{ '--zg': zoneGrow[1], order: 5 }}><summary>Carta del campo</summary>
          {identityCard && (
            <section className="field-card" aria-live="polite">
              <div className="field-card-head">
                <div>
                  <span className="field-card-kicker">Carta del campo</span>
                  <b>{identityCard.title}</b>
                  <span>{identityCard.code}</span>
                </div>
                {fieldExtraLoading ? <span className="field-card-loading">aggiorno</span> : null}
              </div>
              <dl>
                {identityCard.rows.map((row) => (
                  <div className="field-card-row" key={row.key}>
                    <dt>{row.key}</dt>
                    <dd>
                      {row.badge ? <span className="field-card-badge">{row.badge}</span> : null}
                      <span>{row.value}</span>
                      {row.note ? <small>{row.note}</small> : null}
                    </dd>
                  </div>
                ))}
              </dl>
              {selectedFieldIsDate ? (
                <div className="date-conv-editor">
                  <label>Convenzione data
                    <select value={selectedDateConvention || 'it_dmy'} disabled={busy}
                            onChange={(e) => saveDateConvention(e.target.value)}>
                      {DATE_CONVENTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </label>
                </div>
              ) : null}
              {selectedFieldCanInherit ? (
                <div className="inherit-editor">
                  <label>Eredita dalla testata
                    <select value={selectedInheritSource} disabled={busy || !testataInheritOptions.length}
                            onChange={(e) => saveInheritFromTestata(e.target.value)}>
                      <option value="">nessuna eredita</option>
                      {testataInheritOptions.map((opt) => (
                        <option key={opt.field_name} value={opt.field_name}>{inheritOptionLabel(opt)}</option>
                      ))}
                    </select>
                  </label>
                </div>
              ) : null}
            </section>
          )}
          </details>
          <div className="zsplit" onMouseDown={(e) => startZoneResize(e, 2, 0)} style={{ order: 2 }} title="Trascina per ridimensionare le zone" />
          <details className="zone" open style={{ '--zg': zoneGrow[2], order: 1 }}><summary>Campi</summary>
          <ul className="fields">
            {displayFieldGroups.map((group) => (
              <React.Fragment key={group.key}>
                <li className="field-group-label" aria-hidden="true">
                  <span>{group.label}</span>
                  <small>{group.items.length}</small>
                </li>
                {group.items.map((f) => {
              const eff = effective.find((r) => r.field_name === f.field_name)
              const regionCount = Number(eff?.n_regions || 0)
              const draftDone = markedDoneFields.has(f.field_name)
              const done = regionCount > 0 || draftDone
              const rowField = isRigaField(f.field_name)
              const inheritedFrom = inheritedTestataFieldName(eff, f)
              // Identita' = etichetta ARXivar + externalId (DEC-0049). La forma si mostra ANCHE, ma marcata
              // come proposta (inferita) o firmata (confermata) - DEC-0050: e' un tipo-valore, non l'identita'.
              const label = fieldDisplayLabel(f)
              const ident = fieldIdentity(f)
              const confirmed = f.form_source === 'confermata'
              const formClass = inheritedFrom ? 'inherited' : (confirmed ? 'confirmed' : 'inferred')
              return (
                <li key={f.field_name}
                    className={(selectedField === f.field_name ? 'sel ' : '') + (done ? 'done' : '')}
                    title="Seleziona il campo e vai alla sua area nella pagina (se gia' marcata)"
                    onClick={() => { setSelectedField(f.field_name); setLastWord(null); setSuggested(null); resetGapTool() }}>
                  <span className="fn">
                    <span className="lbl">{label}</span>
                    <span className="code">{ident}</span>
                  </span>
                  <span className={'form ' + formClass}
                        title={inheritedFrom ? 'eredita dalla testata: ' + inheritedFrom : (confirmed ? 'tipo confermato' : 'tipo proposto (inferito) - da confermare')}>
                    {inheritedFrom ? 'eredita' : `${f.semantic_form || 'testo_generico'} ${confirmed ? 'OK' : '?'}`}
                  </span>
                  {rowField ? <span className="field-card-badge" title="Campo-riga ClasseBox">riga</span> : null}
                  {inheritedFrom ? <span className="field-card-badge" title={'eredita dalla testata: ' + inheritedFrom}>testata</span> : null}
                  {fieldAnchor(f.field_name)
                    ? <span className="anchor-tag" title={'ancora impostata: ' + fieldAnchor(f.field_name)}>
                        anc. {fieldAnchor(f.field_name)}
                        <button className="anchor-x" title="Rimuovi ancora del campo" disabled={busy}
                                onClick={(e) => { e.stopPropagation(); resetFieldAnchors(f) }}>{'\u00d7'}</button>
                      </span>
                    : null}
                  {done ? <span className="badge">{regionCount > 0 ? 'OK ' + regionCount : 'draft'}</span> : null}
                  {done ? <button className="fld-reset" title="Reset su QUESTO documento"
                                  disabled={busy}
                                  onClick={(e) => { e.stopPropagation(); resetField(f) }}>{'reset'}</button> : null}
                  {regionCount > 0 ? <button className="fld-reset-all" title="Reset su TUTTI i campioni (anche Board attiva)"
                                  disabled={busy}
                                  onClick={(e) => { e.stopPropagation(); resetFieldAllSamples(f) }}>tutti</button> : null}
                </li>
              )
                })}
              </React.Fragment>
            ))}
          </ul>
          {!showDone && doneCount > 0 && (
            <p className="hint">{doneCount} coppie gia' fatte nascoste.</p>
          )}
          </details>
          </div>
        </aside>
      </div>

      {showForms && (
        <div className="modal-backdrop" onClick={() => setShowForms(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <b>Conferma tipi {classe && <span className="muted">- {classe}</span>}</b>
              <button onClick={() => setShowForms(false)} title="Chiudi la finestra di conferma tipi">Chiudi</button>
            </div>
            <p className="modal-hint">
              La forma e' il <b>tipo di valore</b>, condivisibile tra piu' campi: cio' che distingue campi con
              lo stesso tipo e' l'<b>ancora</b>, non la forma. Il tipo <b>proposto</b> e' un'inferenza dall'etichetta;
              qui lo <b>confermi</b> o lo correggi. Finche' non e' confermato resta una proposta.
            </p>
            <div className="forms-wrap">
              <table className="forms-tbl">
                <thead><tr><th>Etichetta ARXivar</th><th>externalId</th><th>Tipo (proposto -> conferma)</th><th>Stato</th><th></th></tr></thead>
                <tbody>
                  {markableFields.map((f) => {
                    const cur = formEdits[f.field_name] ?? (f.semantic_form || 'testo_generico')
                    const ok = f.form_source === 'confermata'
                    const inheritedFrom = inheritedTestataFieldName(f)
                    return (
                      <tr key={f.field_name}>
                        <td>{(f.arxivar_label && String(f.arxivar_label).trim()) || f.field_name}</td>
                        <td className="mono">{(f.arxivar_external_id && String(f.arxivar_external_id).trim()) || f.field_name}</td>
                        <td>
                          <select value={cur} disabled={busy}
                                  onChange={(e) => setFormEdits((m) => ({ ...m, [f.field_name]: e.target.value }))}>
                            {formGroups.map((group) => (
                              <optgroup key={group.area} label={group.label}>
                                {group.forms.map((c) => <option key={c.form} value={c.form}>{c.form}</option>)}
                              </optgroup>
                            ))}
                          </select>
                        </td>
                        <td>{inheritedFrom ? <span className="src inherit">testata</span> : (ok ? <span className="src ok">confermata</span> : <span className="src inf">inferita</span>)}</td>
                        <td><button className="btn-confirm" disabled={busy}
                                    title="Conferma il tipo (forma) scelto per questo campo"
                                    onClick={() => confirmFieldForm(f, cur)}>Conferma</button></td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}



